/**
 * Doserne Academy — i18n (RO / EN / NO).
 * Language is stored in localStorage.DOSERNE_LANG and mirrored on the backend user profile.
 *
 * Usage:
 *   const t = useT();
 *   t("welcome_back")   →  "Velkommen tilbake" | "Bun venit înapoi" | "Welcome back"
 *   t("kurs.count", { n: 3 })  →  "3 kurs"
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

export const LANGS = [
  { code: "no", label: "Norsk",   flag: "🇳🇴" },
  { code: "ro", label: "Română",  flag: "🇷🇴" },
  { code: "en", label: "English", flag: "🇬🇧" },
];

const DICT = {
  no: {
    "app.brand": "Doserne Academy",
    "app.tagline": "Internt team-verktøy",
    "app.copyright": "Doserne Stillas AS — Internal team platform",

    // Common
    "common.save": "Lagre",
    "common.cancel": "Avbryt",
    "common.close": "Lukk",
    "common.delete": "Slett",
    "common.edit": "Rediger",
    "common.add": "Legg til",
    "common.back": "Tilbake",
    "common.next": "Neste",
    "common.previous": "Forrige",
    "common.confirm": "Bekreft",
    "common.loading": "Laster…",
    "common.optional": "valgfritt",
    "common.required": "Obligatorisk",
    "common.search": "Søk",
    "common.new": "Ny",
    "common.finish": "Ferdig",
    "common.done": "Ferdig",
    "common.saved": "Lagret",
    "common.error": "Feil",
    "common.min": "min",

    // Welcome / greeting
    "greet.hi": "Hei",
    "greet.what_now": "Hva vil du gjøre i dag?",
    "greet.today_focus": "Fokus i dag",

    // Auth
    "auth.welcome_back": "Velkommen tilbake",
    "auth.enter_email_hint": "Skriv e-posten din — vi sender deg en engangskode.",
    "auth.email": "E-post",
    "auth.password": "Passord",
    "auth.remember_30": "Hold meg innlogget i 30 dager",
    "auth.send_code": "Send kode",
    "auth.enter_code": "Skriv inn koden",
    "auth.code_sent_to": "Vi sendte en 6-sifret kode + innloggingslenke til",
    "auth.code_label": "Kode (6 siffer)",
    "auth.can_use_link": "Du kan også klikke lenken i e-posten",
    "auth.log_in": "Logg inn",
    "auth.use_other_email": "Bruk en annen e-post",
    "auth.resend_code": "Send koden på nytt",
    "auth.invalid_code": "Ugyldig kode",
    "auth.expired_code": "Koden har utløpt",
    "auth.only_for_employees": "Kun for Doserne-ansatte. Trenger du tilgang? Kontakt Bogdan.",
    "auth.log_out": "Logg ut",

    // Nav
    "nav.home": "Hjem",
    "nav.daily_plan": "Daglig plan",
    "nav.projects": "Prosjekter",
    "nav.customers": "Kunder",
    "nav.progress": "Daglig progresjon",
    "nav.colleagues": "Kolleger",
    "nav.underlag": "Underlag",
    "nav.kontroll": "Kontroll",
    "nav.sja": "SJA",
    "nav.lager": "Lager",
    "nav.kurs": "Opplæring",
    "nav.klaer": "Klær",
    "nav.chat": "Meldinger",
    "nav.reports": "Rapporter & forslag",
    "nav.new_badge": "Ny",
    "nav.capacity": "Project Overview",
    "nav.po2": "Project Overview 2",
    "nav.profile": "Min profil",
    "nav.contracts": "Kontrakter",
    "nav.email": "E-post",
    "nav.timesheet": "Regnskapskontroll",
    "nav.timeliste": "Timeliste",
    "ts.months": "jan.,feb.,mar.,apr.,mai,jun.,jul.,aug.,sep.,okt.,nov.,des.",
    "ts.badge": "Regnskapskontroll",
    "ts.title": "Timeliste (måned)",
    "ts.subtitle": "Alle ansattes timer for hele måneden. Klikk på en dag for å se og korrigere arbeidsintervallet.",
    "ts.tab_employees": "Ansatte",
    "ts.tab_projects": "Prosjekter",
    "ts.tab_review": "Sjekk timer",
    "ts.import_btn": "Importer fra Tripletex",
    "ts.import_running": "Importerer...",
    "ts.import_confirm": "Overskrive månedens timer med data fra Tripletex?",
    "ts.import_result_title": "Tripletex-import resultat",
    "ts.import_matched": "Ansatte matchet",
    "ts.import_entries": "Timer importert",
    "ts.import_projects": "Prosjekter koblet",
    "ts.import_unmatched": "Ikke matchet (finnes ikke i /doserne med samme navn/e-post)",
    "ts.import_none_unmatched": "Alle ansatte ble matchet.",
    "ts.import_err": "Import feilet.",
    "ts.sync_history": "Synk-historikk",
    "ts.sync_never": "Ingen synkronisering ennå",
    "ts.sync_last": "Siste synk",
    "ts.sync_auto": "Automatisk",
    "ts.sync_manual": "Manuell",
    "ts.sync_hours": "timer",
    "ts.sync_vacation": "ferie",
    "ts.sync_sick": "sykdom",
    "ts.sync_auto_hint": "Kjører automatisk hver dag kl. 03:00 (Oslo)",
    "ts.link_btn": "Koble ansatte",
    "ts.link_title": "Tripletex-ansatte — manuell kobling",
    "ts.link_desc": "Koble en Tripletex-ansatt til riktig /doserne-bruker når navnet er skrevet ulikt. Koblingen huskes ved alle framtidige synk.",
    "ts.link_unmatched": "Ikke koblet",
    "ts.link_matched": "Koblet",
    "ts.link_choose": "Velg kollega…",
    "ts.link_save": "Koble",
    "ts.link_unlink": "Koble fra",
    "ts.link_saved": "Koblet! Kjør importen på nytt for å hente timene.",
    "ts.link_none_unmatched": "Alle ansatte er koblet 🎉",
    "ts.link_by_email": "e-post",
    "ts.link_by_name": "navn",
    "ts.link_by_manual": "manuell",
    "ts.link_err": "Kunne ikke koble.",
    "ts.sync_names_btn": "Synk navn fra Tripletex",
    "ts.sync_names_title": "Match navn mot Tripletex",
    "ts.sync_names_desc": "Gi /doserne-medlemmer nøyaktig samme navn som i Tripletex. Forhåndsvis først, deretter bruk.",
    "ts.sync_names_preview": "Forhåndsvis endringer",
    "ts.sync_names_none": "Alle navn stemmer allerede med Tripletex 🎉",
    "ts.sync_names_apply": "Bruk {n} endring(er)",
    "ts.sync_names_applied": "Endret navn på {n} medlem(mer) til Tripletex.",
    "ts.sync_names_err": "Navnesynk feilet.",
    "ts.bclog_title": "Historikk betalt-pause-koder",
    "ts.bclog_none": "Ingen koder brukt denne måneden",
    "ts.bclog_who": "Ansatt",
    "ts.bclog_code": "Kode",
    "ts.bclog_project": "Prosjekt",
    "ts.bclog_day": "Dag",
    "ts.bclog_when": "Registrert",
    "ts.bcsum_workers": "ansatte",
    "ts.bcsum_uses": "bruk",
    "ts.bcsum_valid": "Gyldig",
    "ts.bcsum_export": "Eksporter CSV",
    "ts.bcsum_nolabel": "(ingen etikett)",
    "ts.bcsum_all_projects": "Alle prosjekter",
    "ts.col_missing": "Mangler",
    "ts.col_missing_title": "Arbeidsdager uten noen registrering (måned / år)",
    "ts.backfill_btn": "Importer hele året",
    "ts.backfill_confirm": "Importere Tripletex-timer fra 1. januar til i dag? Kjører i bakgrunnen.",
    "ts.backfill_running": "Importerer…",
    "ts.backfill_started": "Import startet! Se fremdrift i Synk-historikk.",
    "ts.review_by_employee": "Etter ansatt",
    "ts.review_by_project": "Etter prosjekt",
    "ts.review_choose_employee": "— velg ansatt —",
    "ts.review_choose_project": "— velg prosjekt —",
    "ts.review_open": "Åpne timene for måneden",
    "ts.review_empty": "Ingen timer for dette valget.",
    "ts.review_pick_first": "Velg en ansatt eller et prosjekt, og åpne månedens timer.",
    "ts.review_break": "pause",
    "ts.weekdays": "Ma,Ti,On,To,Fr,Lø,Sø",
    "ts.col_employee": "Ansatt",
    "ts.col_norm": "Norm",
    "ts.col_ot50": "OT 50 %",
    "ts.col_ot100": "OT 100 %",
    "ts.col_fer": "Ferie",
    "ts.col_boa": "Syk",
    "ts.col_cost": "Kostnad",
    "ts.empty_members": "Ingen ansatte / timer for denne måneden.",
    "ts.payslip_btn_title": "Generer lønnsslipp",
    "ts.legend_normal": "Normale timer",
    "ts.legend_ot50": "OT 50 %",
    "ts.legend_ot100": "OT 100 %",
    "ts.legend_ferie": "Ferie (F)",
    "ts.legend_sick": "Syk (S)",
    "ts.legend_red": "Søndag / rød dag (100 %)",
    "ts.legend_sat": "Lørdag (vanlig dag)",
    "ts.legend_depot": "Depot (uten OT)",
    "ts.depot_tooltip": "Depot — timene betales alltid som normaltimer (ingen 50 % OT)",
    "ts.legend_compare": "Kryss av 2–3 ansatte for sammenligning",
    "ts.ot_rule_note": "Overtid vises på dagen timene jobbes (timer over 7,5t/dag). Reell (godkjent) overtid = alt over 37,5t jobbet i uka; hvis uka ikke når 37,5t (fraværsdag) blir timene normale. Depot teller mot 37,5t-grensen (opptil 7,5t/dag) men blir aldri overtid.",
    "ts.abbr_vacation": "F",
    "ts.abbr_sick": "S",
    "ts.lock_title": "Lås uker (godkjenning)",
    "ts.lock_hint": "— en låst uke kan ikke lenger redigeres av ansatte og tas med på lønnsslippen",
    "ts.lock_locked": "låst",
    "ts.lock_open": "åpen",
    "ts.lock_month_only": "måned",
    "ts.lock_split_hint": "Delt uke — låsing gjelder kun denne månedens dager; resten er åpen for arbeiderne. Overtid beregnes fortsatt for hele uken.",
    "ts.lock_toast_locked": "Uke {range} låst",
    "ts.lock_toast_unlocked": "Uke {range} låst opp",
    "ts.lock_err": "Kunne ikke endre låsing.",
    "ts.cmp_title": "Sammenligning av timer",
    "ts.cmp_clear": "Tøm",
    "ts.cmp_max": "Du kan sammenligne maks 3 ansatte.",
    "ts.cmp_ot50_short": "OT50",
    "ts.cmp_ot100_short": "OT100",
    "ts.day_edit": "Rediger dag",
    "ts.day_total": "Totalt {h}t",
    "ts.day_ot": "{h}t overtid",
    "ts.day_ot_split": "{ot50}t 50% · {ot100}t 100%",
    "ts.seg_activity": "Aktivitet",
    "ts.day_none": "Ingen timer denne dagen.",
    "ts.kind_work": "Arbeid",
    "ts.kind_vacation": "Ferie",
    "ts.kind_sick": "Syk",
    "ts.no_project": "uten prosjekt",
    "ts.add_segment": "Legg til intervall",
    "ts.cancel": "Avbryt",
    "ts.save": "Lagre",
    "ts.seg_deleted": "Intervall slettet.",
    "ts.seg_del_fail": "Kunne ikke slette.",
    "ts.seg_del_confirm": "Slette dette intervallet?",
    "ts.saved": "Endringer lagret.",
    "ts.save_fail": "Kunne ikke lagre endringene.",
    "ts.overlap": "Overlappende intervaller: {a} og {b}. Korriger eller slett ett.",
    "ts.proj_search": "Søk prosjekt etter navn...",
    "ts.proj_none_found": "Fant ingen prosjekter.",
    "ts.proj_none": "Ingen timer på prosjekter denne måneden.",
    "ts.proj_employees": "{n} ansatte",
    "ts.day_one": "dag",
    "ts.day_many": "dager",
    "ts.proj_total": "Total lønnskostnad måned:",
    "ts.payslip_title": "Lønnsslipp",
    "ts.payslip_tax": "Skattetrekk kr",
    "ts.payslip_save": "Lagre",
    "ts.payslip_saved": "Lønnsslipp lagret",
    "ts.payslip_company_bank": "Firma kontonr.",
    "ts.payslip_utlegg_desc": "Beskrivelse",
    "ts.payslip_utlegg_add": "Legg til utlegg",
    "ts.payslip_print": "Skriv ut / PDF",
    "ts.payslip_no_salary": "Den ansatte har ingen bruttolønn/time satt.",
    "ts.payslip_no_salary_hint": "Sett «Bruttolønn / time» i den ansattes profil (Kolleger-siden) og prøv igjen.",
    "ts.payslip_err": "Kunne ikke generere lønnsslippen.",
    "ts.hours_export_title": "Eksporter timer",
    "ts.hours_export_err": "Kunne ikke laste timer.",
    "ts.hours_from": "Fra",
    "ts.hours_to": "Til",
    "ts.hours_show": "Vis",
    "ts.hours_total": "totalt",
    "ts.hours_days": "dager",
    "ts.hours_empty": "Ingen timer i denne perioden.",
    "ts.totals_gross": "Total brutto (måned)",
    "ts.totals_company": "Total bedriftskostnad",
    "ts.totals_emps": "ansatte med lønn",
    "ts.totals_company_hint": "Brutto + feriepenger 12% + pensjon + arbeidsgiveravgift 14,1%",
    "ts.payslip_note": "Lønnsslipp generert fra {n} låste (godkjente) uker. Akkord, feriepenger og utlegg legges til senere.",
    "ts.payslip_warn": "OBS: {weeks} uke(r) er ikke låst — {hours}t vises IKKE på slippen. Lås dem i Ansatte-fanen for å ta dem med.",
    "ts.load_err": "Kunne ikke laste timelisten.",
    "nav.admin": "Admin panel",
    "nav.coming_soon": "Snart",

    // Home
    "home.no_plan_yet": "Ingen plan publisert ennå",
    "home.see_all_plans": "Alle planer",
    "home.today": "I dag",
    "home.latest_plan": "Siste plan",
    "home.whats_new": "Nyheter",
    "home.no_news": "Ingen nyheter enda",
    "home.feed_title": "Fra jobben — sist postet",
    "home.feed_see_all": "Se alle bilder",
    "home.feed_empty": "Ingen bilder postet ennå",

    // Daily plan
    "plan.title": "Daglig plan",
    "plan.subtitle_admin": "Publiser og administrer dagens jobber",
    "plan.subtitle_member": "Se hva teamene skal gjøre i dag",
    "plan.new": "Ny plan",
    "plan.priority.normal": "Vanlig",
    "plan.priority.important": "Viktig",
    "plan.priority.urgent": "Haster",
    "plan.empty": "Ingen plan publisert ennå.",
    "plan.empty_admin_hint": 'Trykk "Ny plan" for å publisere den første.',
    "plan.published_by": "Publisert av",

    // Projects
    "projects.title": "Prosjekter",
    "projects.subtitle": "Alle jobber vi jobber med — koblet til Underlag, Kontroll, SJA og progresjon.",
    "projects.search_placeholder": "Søk navn, kunde, adresse…",
    "projects.all_status": "Alle status",
    "projects.new": "Nytt prosjekt",
    "projects.empty": "Ingen prosjekter funnet.",
    "projects.add_first": "Legg til første prosjekt",
    "projects.status.active": "Aktiv",
    "projects.status.on_hold": "På vent",
    "projects.status.done": "Ferdig",
    "projects.status.archived": "Arkivert",
    "projects.back_to_list": "Tilbake til prosjekter",
    "projects.details_tab": "Detaljer",
    "projects.progress_tab": "Progresjon",

    // Progress
    "progress.title": "Daglig progresjon",
    "progress.subtitle": "Legg til bilder og notater fra jobben — kategoriseres automatisk per dag i prosjektet.",
    "progress.project": "Prosjekt",
    "progress.date": "Dato",
    "progress.today": "I dag",
    "progress.yesterday": "I går",
    "progress.n_days_ago": "{n} dager siden",
    "progress.note_optional": "Notat (valgfritt)",
    "progress.note_placeholder": "Kort beskrivelse av hva som ble gjort…",
    "progress.photos": "Bilder",
    "progress.add_photo": "Legg til",
    "progress.publish": "Publiser",
    "progress.select_project_first": "Velg et prosjekt over for å se tidligere innlegg.",
    "progress.empty_project": "Ingen progresjon publisert enda.",
    "progress.earlier_posts": "Tidligere innlegg — dette prosjektet",
    "progress.published_success": "Progresjon publisert! 🎉",
    "progress.select_project_error": "Velg et prosjekt",
    "progress.add_content_error": "Legg til minst ett bilde eller en notat",

    // Kurs
    "kurs.title": "Opplæring",
    "kurs.subtitle": "Interne kurs for Doserne-ansatte. Bestå for å låse opp neste kurs.",
    "kurs.status_line": "{done} av {total} kurs bestått",
    "kurs.your_training": "Din opplæringsstatus",
    "kurs.n_certificates": "{n} sertifikat",
    "kurs.n_certificates_plural": "{n} sertifikater",
    "kurs.start_here": "Start her",
    "kurs.status.not_started": "Ikke startet",
    "kurs.status.in_progress": "Pågår",
    "kurs.status.passed": "Bestått",
    "kurs.badge.mandatory": "Obligatorisk",
    "kurs.badge.kurs_n": "Kurs {n}",
    "kurs.lesson_count": "{n} leksjoner",
    "kurs.quiz_count": "{n} spørsmål",
    "kurs.start_course": "Start kurs",
    "kurs.continue": "Fortsett",
    "kurs.locked": "Krever forrige kurs bestått",
    "kurs.coming_soon": "Kommer snart",
    "kurs.passed_with": "Bestått — {pct}%",
    "kurs.step_n_of": "Steg {current} / {total}",
    "kurs.lesson_n_of": "Leksjon {n} · {course}",
    "kurs.lesson_x_of_y": "Leksjon {current} av {total}",
    "kurs.intro_ready": "Introduksjonen er klar til å fullføres",
    "kurs.done_bang": "Ferdig!",
    "kurs.you_finished_prefix": "Du har fullført",
    "kurs.back_to_catalog": "Tilbake til kurs",
    "kurs.lessons_in_this_course": "Leksjoner i dette kurset",
    "kurs.lesson_shortcut": "Leksjon {n}",
    "kurs.n_questions": "{n} spørsmål på slutten",
    "kurs.no_quiz": "Ingen test",
    "kurs.passed_badge": "Fullført",
    "kurs.mini_quiz_title": "Sluttspørsmål",
    "kurs.mini_quiz_intro": "Svar på {n} korte spørsmål — bestå med minst {pct}% for å gå videre.",
    "kurs.mini_quiz_pass": "Bestått!",
    "kurs.mini_quiz_retry_needed": "Ikke bestått enda",
    "kurs.mini_quiz_score": "Din poengsum: {score}% (minimum {threshold}%)",
    "kurs.continue_next": "Fortsett til neste leksjon",
    "kurs.continue_to_quiz": "Fortsett til testen",
    "kurs.back_to_index": "Tilbake til oversikten",
    "kurs.back_to_lesson": "Tilbake til leksjonen",
    "kurs.next_question": "Neste spørsmål",
    "kurs.result_score": "Din poengsum",
    "kurs.result_score_detail": "{correct} av {total} riktige",
    "kurs.result_completed_on": "Fullført",
    "kurs.result_min_pass": "Minimum {pct}% for å bestå",
    "kurs.mini_quiz_reread_hint": "Les gjennom leksjonen på nytt før du prøver igjen. Ta deg tid til hver seksjon.",
    "kurs.mini_quiz_retry_lesson": "Les leksjonen på nytt og prøv igjen",
    "push.on": "Varsler på",
    "push.enable": "Slå på varsler",
    "push.blocked": "Varsler blokkert",
    "push.blocked_help": "Tillatelsen ble avslått. Endre den i nettleserinnstillingene.",
    "push.toast.enabled": "Varsler aktivert — du får beskjed når en ny plan publiseres",
    "push.toast.disabled": "Varsler deaktivert",
    "push.toast.failed": "Kunne ikke aktivere",
    "kurs.next_lesson": "Neste leksjon",
    "kurs.start_quiz": "Start quiz",
    "kurs.quiz_title": "Endelig quiz",
    "kurs.quiz_headline": "Test kunnskapen din",
    "kurs.quiz_intro": "{n} spørsmål · Bestå med minst {pct}%",
    "kurs.quiz_question_n": "Spørsmål {n} av {total}",
    "kurs.quiz_submit": "Send inn",
    "kurs.quiz_answer_all": "Svar på alle spørsmålene for å sende inn.",
    "kurs.quiz_passed": "Bestått! 🎉",
    "kurs.quiz_failed": "Ikke bestått",
    "kurs.quiz_score_line": "Du fikk {correct} av {total} spørsmål riktig ({pct}%). Grense: {threshold}%.",
    "kurs.quiz_correct_answer": "Riktig svar",
    "kurs.quiz_retry": "Prøv igjen",
    "kurs.cert.title": "Sertifikat",
    "kurs.cert.open_pdf": "Åpne sertifikat (PDF)",
    "kurs.cert.my": "Mine sertifikater",
    "kurs.cert.my_subtitle": "Bevis for kurs du har bestått",
    "kurs.cert.empty": "Ingen sertifikater ennå.",
    "kurs.cert.see_courses": "Se tilgjengelige kurs",
    "kurs.locked_page.hint": "Dette kurset låses opp når du har bestått: {prereqs}",
    "kurs.note": "Merk",
    "kurs.warning": "Advarsel",
    "kurs.you_are_here": "Du er her",

    // Profile
    "profile.title": "Min profil",
    "profile.subtitle": "Innstillinger, meny og kurs",
    "profile.tab.overview": "Oversikt",
    "profile.tab.menu": "Meny",
    "profile.tab.kurs": "Kurs",
    "profile.tab.devices": "Enheter",
    "profile.tab.language": "Språk",
    "profile.menu.hint": "Reorganiser eller skjul menyoppføringer. Endringene lagres for din bruker.",
    "profile.menu.save": "Lagre meny",
    "profile.menu.saved": "Meny lagret",
    "profile.menu_side": "Menyplassering",
    "profile.menu_side.left": "Venstre",
    "profile.menu_side.right": "Høyre",
    "profile.language.title": "Velg språk",
    "profile.language.hint": "Språket brukes overalt i appen og lagres på kontoen din.",
    "profile.devices.empty": "Ingen enheter registrert enda.",
    "profile.devices.last_seen": "Sist aktiv",
    "profile.role.admin": "Administrator",
    "profile.role.member": "Doserne Academy medlem",
    "profile.kurs.coming_soon_title": "Kurs kommer snart",
    "profile.kurs.coming_soon_body": "Interne kurs, videoer og sertifikater vil vises her.",

    // Onboarding
    "onboard.welcome": "Velkommen til Doserne Academy",
    "onboard.step_intro": "Vi tar deg raskt gjennom to innstillinger — deretter er du klar.",
    "onboard.step1_title": "Velg språket ditt",
    "onboard.step1_hint": "Du kan endre dette senere fra profilen.",
    "onboard.step2_title": "Menyplassering",
    "onboard.step2_hint": "Hvor vil du ha meny-knappen?",
    "onboard.step2_left": "Venstre side",
    "onboard.step2_right": "Høyre side",
    "onboard.step3_title": "Din profil",
    "onboard.step3_hint": "Kort info slik at kollegaene dine ser hvem du er.",
    "onboard.step3_name": "Fullt navn",
    "onboard.step3_phone": "Telefonnummer",
    "onboard.step3_note": "Begge felt kreves. Du kan endre dette senere fra profilen.",
    "onboard.finish": "Kom i gang",
    "onboard.skip": "Hopp over",

    // Klær page
    "klaer.title": "Klær & utstyr",
    "klaer.kicker": "Utstyr",
    "klaer.tab.shop": "Bestill klær",
    "klaer.tab.profile": "Mine mål",
    "klaer.tab.orders": "Bestillinger",
    "klaer.catalog": "Katalog",
    "klaer.catalog.admin_title": "Katalog",
    "klaer.catalog.new_item": "Ny artikkel",
    "klaer.catalog.edit_item": "Rediger artikkel",
    "klaer.catalog.col_name": "Navn",
    "klaer.catalog.col_sizes": "Størrelser",
    "klaer.catalog.col_colors": "Farger",
    "klaer.catalog.col_status": "Status",
    "klaer.catalog.status_active": "Aktiv",
    "klaer.catalog.status_hidden": "Skjult",
    "klaer.catalog.field_name": "Navn",
    "klaer.catalog.field_category": "Kategori",
    "klaer.catalog.category_placeholder": "F.eks. Klær, Sko, Verneutstyr",
    "klaer.catalog.order_categories": "Rekkefølge på kategorier",
    "klaer.catalog.upload_image": "Last opp bilde",
    "klaer.catalog.uploading": "Laster opp…",
    "klaer.catalog.image_uploaded": "Bilde lastet opp",
    "klaer.catalog.remove_image": "Fjern bilde",
    "klaer.catalog.primary": "Hoved",
    "klaer.shop.all_categories": "Alle",
    "klaer.catalog.field_sizes": "Størrelser (kommaseparert)",
    "klaer.catalog.field_has_color": "Har fargevalg",
    "klaer.catalog.field_colors": "Farger (kommaseparert)",
    "klaer.catalog.field_image": "Bilde-URL (valgfritt)",
    "klaer.catalog.field_note": "Notat (valgfritt)",
    "klaer.catalog.field_order": "Rekkefølge (lavere = først)",
    "klaer.catalog.show_in_shop": "Vis i shoppen",
    "klaer.catalog.color_placeholder": "Sort, Blå, Grå",
    "klaer.catalog.item_saved": "Artikkel oppdatert",
    "klaer.catalog.item_created": "Artikkel opprettet",
    "klaer.catalog.item_deleted": "Slettet",
    "klaer.catalog.save_failed": "Feilet",
    "klaer.catalog.confirm_delete": "Slette denne artikkelen?",
    "klaer.catalog.confirm_delete_permanent": "Slette PERMANENT? Dette kan ikke angres:",
    "klaer.profile.title_sizes": "Mine størrelser",
    "klaer.profile.hint_sizes": "Fylles automatisk inn i shoppen når du bestiller — men du kan alltids overstyre i skjemaet.",
    "klaer.profile.title_body": "Kroppsmål (valgfritt)",
    "klaer.profile.hint_body": "Nyttig hvis vi bestiller uniform på mål.",
    "klaer.profile.height": "Høyde (cm)",
    "klaer.profile.waist": "Midje (cm)",
    "klaer.profile.leg": "Innerbein (cm)",
    "klaer.profile.shoe_eu": "Sko (EU)",
    "klaer.profile.title_color": "Foretrukket farge",
    "klaer.profile.color_placeholder": "f.eks. Sort",
    "klaer.profile.select": "— velg —",
    "klaer.profile.save_btn": "Lagre målene mine",
    "klaer.profile.saved": "Målene dine er lagret",
    "klaer.profile.save_failed": "Lagring feilet",
    "klaer.shop.sizes_label": "Størrelser:",
    "klaer.shop.colors_label": "Farger:",
    "klaer.order.kicker": "Bestill",
    "klaer.order.size": "Størrelse",
    "klaer.order.color": "Farge",
    "klaer.order.quantity": "Antall",
    "klaer.order.note": "Kommentar (valgfritt)",
    "klaer.order.note_placeholder": "f.eks. Høyt skaft, sommermodell...",
    "klaer.order.submit": "Send bestilling",
    "klaer.order.placed": "{name} bestilt",
    "klaer.order.failed": "Bestilling feilet",
    "klaer.orders.tab_incoming": "Innkomne bestillinger",
    "klaer.orders.tab_mine": "Mine bestillinger",
    "klaer.orders.filter_all": "Alle",
    "klaer.orders.filter_new": "Under behandling",
    "klaer.orders.filter_delivered": "Mottatt",
    "klaer.orders.empty_delivered": "Ingen mottatte bestillinger ennå.",
    "klaer.orders.empty_new": "Ingen ventende bestillinger.",
    "klaer.orders.empty_mine": "Du har ikke bestilt noe ennå.",
    "klaer.orders.empty_incoming": "Ingen innkomne bestillinger.",
    "klaer.orders.badge_new": "NY",
    "klaer.orders.badge_delivered": "LEVERT",
    "klaer.orders.pcs": "stk",
    "klaer.orders.deliver_confirm": "Marker som levert til {name}?",
    "klaer.orders.delivered_at": "Mottatt {when}",
    "klaer.orders.delivered_by": "av {who}",
    "klaer.orders.deliver_btn": "Levert",
    "klaer.orders.reopen_title": "Gjenåpne",
    "klaer.orders.delivered_toast": "Merket som levert",
    "klaer.orders.reopen_toast": "Bestilling gjenåpnet",
    "klaer.orders.delete_confirm": "Slette bestillingen?",
    "klaer.orders.deleted_toast": "Slettet",
    "klaer.orders.op_failed": "Feilet",
    "klaer.admin_catalog_btn": "Administrer katalog",
    "klaer.cart.kicker": "Bestilling",
    "klaer.cart.title": "Handlekurv",
    "klaer.cart.empty": "Kurven er tom — legg til noen plagg fra shoppen.",
    "klaer.cart.add_btn": "Legg i kurven",
    "klaer.cart.added_toast": "{name} lagt i kurven",
    "klaer.cart.submit_btn": "Send bestilling",
    "klaer.cart.sent_toast": "Bestilling sendt ({count} stk)",
    "klaer.cart.clear": "Tøm kurven",
    "klaer.cart.remove": "Fjern",
    "klaer.cart.order_note": "Kommentar til hele bestillingen (valgfritt)",
    "klaer.cart.order_note_placeholder": "f.eks. Trengs før neste mandag...",

    // Chat / Messenger
    "chat.action.reply": "Svar",
    "chat.action.mark_project": "Merk til prosjekt",
    "chat.action.delete": "Slett",
    "chat.composer.placeholder": "Skriv en melding…",
    "chat.composer.send": "Send",
    "chat.composer.voice_note": "Talenotat",
    "chat.composer.photo": "Foto",
    "chat.composer.emoji": "Emoji",
    "chat.reply.replying_to": "Svarer",
    "chat.reply.cancel": "Avbryt",
    "chat.header.project": "Prosjekt",
    "chat.header.group": "Gruppe",
    "chat.header.members": "medlemmer",
    "chat.header.online": "Online",
    "chat.header.last_seen": "Sist sett",
    "chat.toast.deleted": "Slettet",
    "chat.toast.failed": "Feilet",
    "chat.toast.muted_on": "Samtalen er dempet",
    "chat.toast.muted_off": "Lyd på igjen",
    "chat.toast.upload_failed": "Opplasting feilet",
    "chat.toast.voice_failed": "Kunne ikke sende talenotat",
    "chat.confirm.delete_message": "Slette meldingen?",
    "chat.marker.no_projects": "Ingen prosjekter",
    "chat.marker.title": "Merk til prosjekt",
    "chat.marker.search": "Søk prosjekt…",
    "chat.reactions.count": "reagerte",

    // Chat — extended
    "chat.header.dm": "Direktemelding",
    "chat.header.active_now": "Aktiv nå",
    "chat.header.members_count": "{count} medlemmer",
    "chat.header.mute": "Demp samtalen",
    "chat.header.unmute": "Slå på lyd",
    "chat.composer.placeholder_short": "Skriv…",
    "chat.composer.send_photo": "Send bilde",
    "chat.composer.hold_to_record": "Hold for å ta opp talenotat",
    "chat.composer.send_like": "Send 👍",
    "chat.audio.play": "Spill av",
    "chat.audio.pause": "Pause",
    "chat.message.deleted": "Meldingen er slettet",
    "chat.message.photo_alt": "foto",
    "chat.time.just_now": "nå",
    "chat.time.locale": "no-NO",
    "chat.toast.send_failed": "Send feilet",
    "chat.newroom.tab_dm": "Direktemelding",
    "chat.newroom.tab_group": "Gruppe",
    "chat.newroom.group_name_placeholder": "Gruppenavn (f.eks. Team A · Suhms Gate)",
    "chat.newroom.search_colleague": "Søk kollega…",
    "chat.newroom.select_colleague": "Velg en kollega",
    "chat.newroom.fill_group": "Fyll inn navn og velg minst én kollega",
    "chat.newroom.create_btn": "Opprett",

    // Chat FAB / drawer
    "fab.open_full": "Åpne full chat",
    "fab.minimize": "Minimer",
    "fab.close": "Lukk",
    "fab.open_msg": "Åpne meldinger",
    "fab.new_conversation": "Ny samtale",
    "fab.mute_all": "Slå av lyd for alle",
    "fab.unmute_all": "Slå på lyd",
    "fab.search_convos": "Søk i samtaler",
    "fab.no_messages": "Ingen meldinger",
    "fab.enable_push": "Aktiver varsler",
    "fab.push_enabled": "Push aktivert — du får varsler for chat",
    "fab.push_failed": "Kunne ikke aktivere",
    "fab.dm_open_failed": "Kunne ikke starte samtalen",
    "fab.project_chat_failed": "Kunne ikke åpne prosjektchat",
    "fab.chat_tab": "Chat",

    // Missing-phone banner (Home)
    "banner.phone.title": "Legg til telefonnummeret ditt",
    "banner.phone.subtitle": "Kollegene dine trenger dette for å ringe eller sende SMS. Det tar 5 sekunder.",
    "banner.phone.placeholder": "+47 123 45 678",
    "banner.phone.save": "Lagre",
    "banner.phone.saved": "Tusen takk! Kollegene dine kan nå nå deg på {phone}.",

    // PWA install
    "install.button": "Installer Doserne-appen",
    "install.ios_hint": "Trykk delings-knappen (□↑) nederst i Safari, og velg «Legg til på Hjem-skjerm». Ikonet «Doserne Academy» dukker opp på Hjem-skjermen din.",
    "install.generic_hint": "I nettleseren din: åpne meny (⋮) → «Installer app» eller «Legg til på Hjem-skjerm».",

    // ------- Week Calendar -------
    "cal.today": "I dag",
    "cal.week": "Uke",
    "cal.empty_week": "Ingen notater denne uken — klikk en dag for å legge til.",
    "cal.add_event": "Legg til hendelse",
    "cal.no_events": "Ingen hendelser for denne dagen.",
    "cal.title_ph": "Tittel",
    "cal.notes_ph": "Notater / beskrivelse…",
    "cal.date": "Dato",
    "cal.start_time": "Starttid",
    "cal.end_time": "Sluttid",
    "cal.date_tt": "Dagen hendelsen gjelder",
    "cal.start_tt": "Når den starter. Tom = hele dagen. Påminnelsen beregnes fra denne tiden.",
    "cal.end_tt": "Valgfritt — vises bare som intervall",
    "cal.allday_hint": "💡 Blanke = «hele dagen»-hendelse. Påminnelser bruker Starttid.",
    "cal.color": "Farge",
    "cal.save": "Lagre",
    "cal.cancel": "Avbryt",
    "cal.delete_q": "Slett denne hendelsen?",
    "cal.deleted": "Slettet",
    "cal.saved": "Lagret",
    "cal.title_required": "Tittel er påkrevd",
    "cal.kind_note": "Notat",
    "cal.kind_meeting": "Møte",
    "cal.kind_private": "Privat",
    "cal.kind_share": "Del",
    "cal.share_header": "Del med administratorer",
    "cal.share_loading": "Laster kolleger…",
    "cal.share_empty": "Ingen administrator tilgjengelig.",
    "cal.share_summary_one": "Vil få påminnelse: 1 administrator",
    "cal.share_summary_many": "Vil få påminnelse: {{n}} administratorer",
    "cal.remind": "Påminnelse",
    "cal.remind_tt": "Hvor lenge før starten en varsling dukker opp i bjellen",
    "cal.remind_off": "Av",
    "cal.remind_at_start": "Ved start",
    "cal.remind_5m": "5 min før",
    "cal.remind_15m": "15 min før",
    "cal.remind_30m": "30 min før",
    "cal.remind_1h": "1 time før",
    "cal.remind_1d": "1 dag før",
    "cal.pop_kicker": "Påminnelse",
    "cal.pop_dismiss": "Lukk",
    "cal.pop_snooze_5": "Utsett 5 min",
    "cal.pop_snooze_15": "Utsett 15 min",
    "cal.pop_open": "Åpne hendelsen",
    "cal.pop_snoozed_5": "Utsatt 5 min",
    "cal.pop_snoozed_15": "Utsatt 15 min",
    "cal.pop_dismissed": "Lukket",
    "cal.jump_title": "Hopp til dato",
    "cal.jump_today": "I dag",
    "cal.agenda_today": "I dag — hva venter",
    "cal.agenda_next": "Neste på kalenderen",
    "cal.allday_short": "Hele dagen",
  },

  ro: {
    "app.brand": "Doserne Academy",
    "app.tagline": "Instrument intern pentru echipă",
    "app.copyright": "Doserne Stillas AS — Platformă internă a echipei",

    "common.save": "Salvează",
    "common.cancel": "Anulează",
    "common.close": "Închide",
    "common.delete": "Șterge",
    "common.edit": "Editează",
    "common.add": "Adaugă",
    "common.back": "Înapoi",
    "common.next": "Următor",
    "common.previous": "Anterior",
    "common.confirm": "Confirmă",
    "common.loading": "Se încarcă…",
    "common.optional": "opțional",
    "common.required": "Obligatoriu",
    "common.search": "Caută",
    "common.new": "Nou",
    "common.finish": "Gata",
    "common.done": "Gata",
    "common.saved": "Salvat",
    "common.error": "Eroare",
    "common.min": "min",

    "greet.hi": "Salut",
    "greet.what_now": "Ce vrei să faci azi?",
    "greet.today_focus": "Focus azi",

    "auth.welcome_back": "Bun venit înapoi",
    "auth.enter_email_hint": "Introdu emailul — îți trimitem un cod de utilizare unică.",
    "auth.email": "Email",
    "auth.password": "Parolă",
    "auth.remember_30": "Ține-mă conectat 30 de zile",
    "auth.send_code": "Trimite codul",
    "auth.enter_code": "Introdu codul",
    "auth.code_sent_to": "Am trimis codul de 6 cifre + link de conectare la",
    "auth.code_label": "Cod (6 cifre)",
    "auth.can_use_link": "Poți da click și pe linkul din email",
    "auth.log_in": "Conectează-te",
    "auth.use_other_email": "Folosește alt email",
    "auth.resend_code": "Retrimite codul",
    "auth.invalid_code": "Cod invalid",
    "auth.expired_code": "Codul a expirat",
    "auth.only_for_employees": "Doar pentru angajații Doserne. Ai nevoie de acces? Contactează Bogdan.",
    "auth.log_out": "Deconectează-te",

    "nav.home": "Acasă",
    "nav.daily_plan": "Plan zilnic",
    "nav.projects": "Proiecte",
    "nav.customers": "Clienți",
    "nav.progress": "Progres zilnic",
    "nav.colleagues": "Colegi",
    "nav.underlag": "Underlag",
    "nav.kontroll": "Kontroll",
    "nav.sja": "SJA",
    "nav.admin": "Panou admin",
    "nav.lager": "Depozit",
    "nav.kurs": "Instruire",
    "nav.klaer": "Haine",
    "nav.chat": "Mesaje",
    "nav.reports": "Sesizări & sugestii",
    "nav.new_badge": "Nou",
    "nav.capacity": "Project Overview",
    "nav.po2": "Project Overview 2",
    "nav.profile": "Profilul meu",
    "nav.contracts": "Contracte",
    "nav.email": "Email",
    "nav.timesheet": "Control Contabil",
    "nav.timeliste": "Pontaj",
    "ts.months": "ian.,feb.,mar.,apr.,mai,iun.,iul.,aug.,sep.,oct.,nov.,dec.",
    "ts.badge": "Control Contabil",
    "ts.title": "Pontaj lunar",
    "ts.subtitle": "Orele tuturor angajaților pe toată luna. Click pe o zi pentru a vedea și corecta intervalul de lucru.",
    "ts.tab_employees": "Angajați",
    "ts.tab_projects": "Proiecte",
    "ts.tab_review": "Verifică ore",
    "ts.import_btn": "Importă din Tripletex",
    "ts.import_running": "Se importă...",
    "ts.import_confirm": "Suprascrii orele lunii curente cu cele din Tripletex?",
    "ts.import_result_title": "Rezultat import Tripletex",
    "ts.import_matched": "Angajați potriviți",
    "ts.import_entries": "Ore importate",
    "ts.import_projects": "Proiecte legate",
    "ts.import_unmatched": "Nepotriviți (nu există în /doserne cu același nume/email)",
    "ts.import_none_unmatched": "Toți angajații au fost potriviți.",
    "ts.import_err": "Importul a eșuat.",
    "ts.sync_history": "Istoric sincronizări",
    "ts.sync_never": "Încă nicio sincronizare",
    "ts.sync_last": "Ultima sincronizare",
    "ts.sync_auto": "Automat",
    "ts.sync_manual": "Manual",
    "ts.sync_hours": "ore",
    "ts.sync_vacation": "concediu",
    "ts.sync_sick": "boală",
    "ts.sync_auto_hint": "Rulează automat zilnic la 03:00 (Oslo)",
    "ts.link_btn": "Legare angajați",
    "ts.link_title": "Angajați Tripletex — legare manuală",
    "ts.link_desc": "Leagă un angajat Tripletex de contul /doserne corect când numele e scris diferit. Legătura e reținută la toate sincronizările viitoare.",
    "ts.link_unmatched": "Nepotriviți",
    "ts.link_matched": "Potriviți",
    "ts.link_choose": "Alege colegul…",
    "ts.link_save": "Leagă",
    "ts.link_unlink": "Dezleagă",
    "ts.link_saved": "Legat! Rulează din nou importul ca să apară orele.",
    "ts.link_none_unmatched": "Toți angajații sunt legați 🎉",
    "ts.link_by_email": "email",
    "ts.link_by_name": "nume",
    "ts.link_by_manual": "manual",
    "ts.link_err": "Legarea a eșuat.",
    "ts.sync_names_btn": "Sincronizează nume din Tripletex",
    "ts.sync_names_title": "Potrivește numele cu Tripletex",
    "ts.sync_names_desc": "Redenumește membrii /doserne exact ca în Tripletex. Vezi întâi previzualizarea, apoi aplică.",
    "ts.sync_names_preview": "Vezi modificările",
    "ts.sync_names_none": "Toate numele coincid deja cu Tripletex 🎉",
    "ts.sync_names_apply": "Aplică {n} redenumire/redenumiri",
    "ts.sync_names_applied": "Am redenumit {n} membri ca în Tripletex.",
    "ts.sync_names_err": "Sincronizarea numelor a eșuat.",
    "ts.bclog_title": "Istoric coduri pauză folosite",
    "ts.bclog_none": "Niciun cod folosit în această lună",
    "ts.bclog_who": "Angajat",
    "ts.bclog_code": "Cod",
    "ts.bclog_project": "Proiect",
    "ts.bclog_day": "Ziua",
    "ts.bclog_when": "Înregistrat",
    "ts.bcsum_workers": "angajați",
    "ts.bcsum_uses": "folosiri",
    "ts.bcsum_valid": "Valabil",
    "ts.bcsum_export": "Export CSV",
    "ts.bcsum_nolabel": "(fără etichetă)",
    "ts.bcsum_all_projects": "Toate proiectele",
    "ts.col_missing": "Lipsă",
    "ts.col_missing_title": "Zile lucrătoare fără nicio înregistrare (lună / an)",
    "ts.backfill_btn": "Importă tot anul",
    "ts.backfill_confirm": "Import Tripletex din 1 ianuarie până azi? Rulează în fundal.",
    "ts.backfill_running": "Se importă…",
    "ts.backfill_started": "Import pornit! Vezi progresul în Istoric sincronizări.",
    "ts.review_by_employee": "După angajat",
    "ts.review_by_project": "După proiect",
    "ts.review_choose_employee": "— alege angajat —",
    "ts.review_choose_project": "— alege proiect —",
    "ts.review_open": "Deschide orele pe lună",
    "ts.review_empty": "Nicio oră pentru această selecție.",
    "ts.review_pick_first": "Alege un angajat sau un proiect, apoi deschide orele lunii.",
    "ts.review_break": "pauză",
    "ts.weekdays": "L,Ma,Mi,J,V,S,D",
    "ts.col_employee": "Angajat",
    "ts.col_norm": "Norm",
    "ts.col_ot50": "OV 1.5×",
    "ts.col_ot100": "OV 2×",
    "ts.col_fer": "Fer",
    "ts.col_boa": "Boa",
    "ts.col_cost": "Cost",
    "ts.empty_members": "Niciun angajat / pontaj pentru această lună.",
    "ts.payslip_btn_title": "Generează fișă de salariu",
    "ts.legend_normal": "Ore normale",
    "ts.legend_ot50": "OV 1.5× (50%)",
    "ts.legend_ot100": "OV 2× (100%)",
    "ts.legend_ferie": "Ferie (F)",
    "ts.legend_sick": "Boală (S)",
    "ts.legend_red": "Duminică / zi roșie (100%)",
    "ts.legend_sat": "Sâmbătă (zi normală)",
    "ts.legend_depot": "Depozit (fără OT)",
    "ts.depot_tooltip": "Depozit — orele se plătesc mereu ca normale (fără OT 1.5×)",
    "ts.legend_compare": "Bifează 2–3 angajați pentru comparație",
    "ts.ot_rule_note": "Overtime-ul apare pe ziua în care se lucrează (orele peste 7.5h/zi). OV real (aprobat) = tot ce trece de 37.5h lucrate pe săptămână; dacă săptămâna nu atinge 37.5h (zi lipsă) orele rămân normale. Depozitul contează la pragul de 37.5h (max 7.5h/zi) dar nu devine niciodată overtime.",
    "ts.abbr_vacation": "F",
    "ts.abbr_sick": "S",
    "ts.lock_title": "Blocare săptămâni (aprobare)",
    "ts.lock_hint": "— o săptămână blocată nu mai poate fi editată de angajați și intră pe fișa de salariu",
    "ts.lock_locked": "blocat",
    "ts.lock_open": "deschis",
    "ts.lock_month_only": "lună",
    "ts.lock_split_hint": "Săptămână împărțită — blocarea afectează doar zilele din luna curentă; restul rămâne deschis pentru băieți. Overtime-ul se calculează în continuare pe toată săptămâna.",
    "ts.lock_toast_locked": "Săptămâna {range} blocată",
    "ts.lock_toast_unlocked": "Săptămâna {range} deblocată",
    "ts.lock_err": "Nu s-a putut schimba blocarea.",
    "ts.cmp_title": "Comparație ore lucrate",
    "ts.cmp_clear": "Golește",
    "ts.cmp_max": "Poți compara maxim 3 angajați.",
    "ts.cmp_ot50_short": "OV1.5",
    "ts.cmp_ot100_short": "OV2",
    "ts.day_edit": "Editare zi",
    "ts.day_total": "Total {h}t",
    "ts.day_ot": "{h}t overtime",
    "ts.day_ot_split": "{ot50}t 50% · {ot100}t 100%",
    "ts.seg_activity": "Activitate",
    "ts.day_none": "Nicio oră în această zi.",
    "ts.kind_work": "Lucru",
    "ts.kind_vacation": "Ferie",
    "ts.kind_sick": "Boală",
    "ts.no_project": "fără proiect",
    "ts.add_segment": "Adaugă interval",
    "ts.cancel": "Anulează",
    "ts.save": "Salvează",
    "ts.seg_deleted": "Interval șters.",
    "ts.seg_del_fail": "Nu s-a putut șterge.",
    "ts.seg_del_confirm": "Ștergi acest interval?",
    "ts.saved": "Modificări salvate.",
    "ts.save_fail": "Nu s-au putut salva modificările.",
    "ts.overlap": "Intervale suprapuse: {a} și {b}. Corectează sau șterge unul.",
    "ts.proj_search": "Caută proiect după nume...",
    "ts.proj_none_found": "Niciun proiect găsit.",
    "ts.proj_none": "Nicio oră pe proiecte în această lună.",
    "ts.proj_employees": "{n} angajați",
    "ts.day_one": "zi",
    "ts.day_many": "zile",
    "ts.proj_total": "Total manoperă lună:",
    "ts.payslip_title": "Fișă de salariu",
    "ts.payslip_tax": "Skattetrekk kr",
    "ts.payslip_save": "Salvează",
    "ts.payslip_saved": "Fișă salvată",
    "ts.payslip_company_bank": "Cont firmă",
    "ts.payslip_utlegg_desc": "Descriere",
    "ts.payslip_utlegg_add": "Adaugă cheltuială",
    "ts.payslip_print": "Printează / PDF",
    "ts.payslip_no_salary": "Angajatul nu are salariu brut/oră setat.",
    "ts.payslip_no_salary_hint": "Setează „Salariu brut / oră” în profilul angajatului (pagina Colegi) și încearcă din nou.",
    "ts.payslip_err": "Nu s-a putut genera fișa.",
    "ts.hours_export_title": "Export ore",
    "ts.hours_export_err": "Nu s-au putut încărca orele.",
    "ts.hours_from": "De la",
    "ts.hours_to": "Până la",
    "ts.hours_show": "Arată",
    "ts.hours_total": "total",
    "ts.hours_days": "zile",
    "ts.hours_empty": "Nicio oră în această perioadă.",
    "ts.totals_gross": "Total brut (lună)",
    "ts.totals_company": "Total cost companie",
    "ts.totals_emps": "angajați cu salariu",
    "ts.totals_company_hint": "Brut + feriepenger 12% + pensjon + arbeidsgiveravgift 14,1%",
    "ts.payslip_note": "Fișă generată din {n} săptămâni blocate (aprobate). Akkord, feriepenger și utlegg vor fi adăugate ulterior.",
    "ts.payslip_warn": "Atenție: {weeks} săptămâni nu sunt blocate — {hours}h NU apar pe fișă. Blochează-le în tab-ul Angajați ca să le incluzi.",
    "ts.load_err": "Nu s-a putut încărca pontajul.",
    "nav.coming_soon": "Curând",

    "home.no_plan_yet": "Niciun plan publicat încă",
    "home.see_all_plans": "Toate planurile",
    "home.today": "Azi",
    "home.latest_plan": "Ultimul plan",
    "home.whats_new": "Ce e nou",
    "home.no_news": "Nu sunt noutăți încă",
    "home.feed_title": "De pe șantier — ultimele poze",
    "home.feed_see_all": "Vezi toate pozele",
    "home.feed_empty": "Nu s-au postat poze încă",

    "plan.title": "Plan zilnic",
    "plan.subtitle_admin": "Publică și administrează treburile zilei",
    "plan.subtitle_member": "Vezi ce trebuie să facă echipele azi",
    "plan.new": "Plan nou",
    "plan.priority.normal": "Normal",
    "plan.priority.important": "Important",
    "plan.priority.urgent": "Urgent",
    "plan.empty": "Niciun plan publicat încă.",
    "plan.empty_admin_hint": 'Apasă "Plan nou" pentru a publica primul.',
    "plan.published_by": "Publicat de",

    "projects.title": "Proiecte",
    "projects.subtitle": "Toate lucrările la care lucrăm — legate de Pontaj, Control, SJA și progres.",
    "projects.search_placeholder": "Caută nume, client, adresă…",
    "projects.all_status": "Toate statusurile",
    "projects.new": "Proiect nou",
    "projects.empty": "Niciun proiect găsit.",
    "projects.add_first": "Adaugă primul proiect",
    "projects.status.active": "Activ",
    "projects.status.on_hold": "În așteptare",
    "projects.status.done": "Terminat",
    "projects.status.archived": "Arhivat",
    "projects.back_to_list": "Înapoi la proiecte",
    "projects.details_tab": "Detalii",
    "projects.progress_tab": "Progres",

    "progress.title": "Progres zilnic",
    "progress.subtitle": "Adaugă poze și note de pe șantier — se catalog automat pe zile în proiect.",
    "progress.project": "Proiect",
    "progress.date": "Data",
    "progress.today": "Azi",
    "progress.yesterday": "Ieri",
    "progress.n_days_ago": "acum {n} zile",
    "progress.note_optional": "Notă (opțional)",
    "progress.note_placeholder": "Scurtă descriere a ce s-a făcut…",
    "progress.photos": "Poze",
    "progress.add_photo": "Adaugă",
    "progress.publish": "Publică",
    "progress.select_project_first": "Alege un proiect mai sus ca să vezi postările anterioare.",
    "progress.empty_project": "Niciun progres publicat încă.",
    "progress.earlier_posts": "Postări anterioare — acest proiect",
    "progress.published_success": "Progres publicat! 🎉",
    "progress.select_project_error": "Alege un proiect",
    "progress.add_content_error": "Adaugă cel puțin o poză sau o notă",

    "kurs.title": "Instruire",
    "kurs.subtitle": "Cursuri interne pentru angajații Doserne. Promovează pentru a debloca cursul următor.",
    "kurs.status_line": "{done} din {total} cursuri promovate",
    "kurs.your_training": "Statusul tău de instruire",
    "kurs.n_certificates": "{n} certificat",
    "kurs.n_certificates_plural": "{n} certificate",
    "kurs.start_here": "Începe aici",
    "kurs.status.not_started": "Neînceput",
    "kurs.status.in_progress": "În progres",
    "kurs.status.passed": "Promovat",
    "kurs.badge.mandatory": "Obligatoriu",
    "kurs.badge.kurs_n": "Curs {n}",
    "kurs.lesson_count": "{n} lecții",
    "kurs.quiz_count": "{n} întrebări",
    "kurs.start_course": "Începe cursul",
    "kurs.continue": "Continuă",
    "kurs.locked": "Necesită cursul anterior promovat",
    "kurs.coming_soon": "În curând",
    "kurs.passed_with": "Promovat — {pct}%",
    "kurs.step_n_of": "Pas {current} / {total}",
    "kurs.lesson_n_of": "Lecția {n} · {course}",
    "kurs.lesson_x_of_y": "Lecția {current} din {total}",
    "kurs.intro_ready": "Introducerea este gata de finalizare",
    "kurs.done_bang": "Gata!",
    "kurs.you_finished_prefix": "Ai finalizat",
    "kurs.back_to_catalog": "Înapoi la cursuri",
    "kurs.lessons_in_this_course": "Lecțiile acestui curs",
    "kurs.lesson_shortcut": "Lecția {n}",
    "kurs.n_questions": "{n} întrebări la final",
    "kurs.no_quiz": "Fără test",
    "kurs.passed_badge": "Promovată",
    "kurs.mini_quiz_title": "Întrebări la finalul lecției",
    "kurs.mini_quiz_intro": "Răspunde la {n} întrebări scurte — promovezi cu minim {pct}% pentru a continua.",
    "kurs.mini_quiz_pass": "Promovat!",
    "kurs.mini_quiz_retry_needed": "Încă nu ai promovat",
    "kurs.mini_quiz_score": "Scorul tău: {score}% (minim necesar {threshold}%)",
    "kurs.continue_next": "Continuă cu lecția următoare",
    "kurs.continue_to_quiz": "Continuă către test",
    "kurs.back_to_index": "Înapoi la lecții",
    "kurs.back_to_lesson": "Înapoi la lecție",
    "kurs.next_question": "Următoarea întrebare",
    "kurs.result_score": "Scorul tău",
    "kurs.result_score_detail": "{correct} din {total} corecte",
    "kurs.result_completed_on": "Finalizat pe",
    "kurs.result_min_pass": "Minim {pct}% pentru promovare",
    "kurs.mini_quiz_reread_hint": "Recitește lecția înainte de a încerca din nou. Ia-ți timp pentru fiecare secțiune.",
    "kurs.mini_quiz_retry_lesson": "Reia lecția și încearcă din nou",
    "push.on": "Notificări activate",
    "push.enable": "Activează notificări",
    "push.blocked": "Notificări blocate",
    "push.blocked_help": "Permisiunea a fost refuzată. Modific-o din setările browser-ului.",
    "push.toast.enabled": "Notificări activate — vei primi când se publică un plan",
    "push.toast.disabled": "Notificări dezactivate",
    "push.toast.failed": "Nu s-a putut activa",
    "kurs.next_lesson": "Lecția următoare",
    "kurs.start_quiz": "Începe quiz-ul",
    "kurs.quiz_title": "Quiz final",
    "kurs.quiz_headline": "Testează-ți cunoștințele",
    "kurs.quiz_intro": "{n} întrebări · Promovează cu minim {pct}%",
    "kurs.quiz_question_n": "Întrebarea {n} din {total}",
    "kurs.quiz_submit": "Trimite",
    "kurs.quiz_answer_all": "Răspunde la toate întrebările pentru a trimite.",
    "kurs.quiz_passed": "Promovat! 🎉",
    "kurs.quiz_failed": "Nu ai promovat",
    "kurs.quiz_score_line": "Ai {correct} din {total} răspunsuri corecte ({pct}%). Prag: {threshold}%.",
    "kurs.quiz_correct_answer": "Răspuns corect",
    "kurs.quiz_retry": "Încearcă din nou",
    "kurs.cert.title": "Certificat",
    "kurs.cert.open_pdf": "Deschide certificat (PDF)",
    "kurs.cert.my": "Certificatele mele",
    "kurs.cert.my_subtitle": "Dovada cursurilor pe care le-ai promovat",
    "kurs.cert.empty": "Niciun certificat încă.",
    "kurs.cert.see_courses": "Vezi cursurile disponibile",
    "kurs.locked_page.hint": "Acest curs se deblochează după ce promovezi: {prereqs}",
    "kurs.note": "Notă",
    "kurs.warning": "Atenție",
    "kurs.you_are_here": "Ești aici",

    "profile.title": "Profilul meu",
    "profile.subtitle": "Setări, meniu și cursuri",
    "profile.tab.overview": "Prezentare",
    "profile.tab.menu": "Meniu",
    "profile.tab.kurs": "Cursuri",
    "profile.tab.devices": "Dispozitive",
    "profile.tab.language": "Limbă",
    "profile.menu.hint": "Reorganizează sau ascunde intrări din meniu. Modificările se salvează pentru contul tău.",
    "profile.menu.save": "Salvează meniul",
    "profile.menu.saved": "Meniu salvat",
    "profile.menu_side": "Poziția meniului",
    "profile.menu_side.left": "Stânga",
    "profile.menu_side.right": "Dreapta",
    "profile.language.title": "Alege limba",
    "profile.language.hint": "Limba se folosește peste tot în aplicație și se salvează pe cont.",
    "profile.devices.empty": "Niciun dispozitiv înregistrat încă.",
    "profile.devices.last_seen": "Ultima activitate",
    "profile.role.admin": "Administrator",
    "profile.role.member": "Membru Doserne Academy",
    "profile.kurs.coming_soon_title": "Cursurile vin în curând",
    "profile.kurs.coming_soon_body": "Cursurile interne, videoclipurile și certificatele vor apărea aici.",

    "onboard.welcome": "Bun venit la Doserne Academy",
    "onboard.step_intro": "Îți setăm rapid 2 preferințe — apoi ești gata de plecare.",
    "onboard.step1_title": "Alege limba",
    "onboard.step1_hint": "O poți schimba oricând din profil.",
    "onboard.step2_title": "Poziția meniului",
    "onboard.step2_hint": "Unde vrei să fie butonul de meniu?",
    "onboard.step2_left": "Stânga",
    "onboard.step2_right": "Dreapta",
    "onboard.step3_title": "Profilul tău",
    "onboard.step3_hint": "Câteva informații rapide pentru colegii tăi.",
    "onboard.step3_name": "Nume complet",
    "onboard.step3_phone": "Telefon",
    "onboard.step3_note": "Ambele câmpuri sunt obligatorii. Le poți modifica ulterior din profil.",
    "onboard.finish": "Începe",
    "onboard.skip": "Sari peste",

    // Klær page (RO)
    "klaer.title": "Haine & echipament",
    "klaer.kicker": "Echipament",
    "klaer.tab.shop": "Comandă haine",
    "klaer.tab.profile": "Măsurile mele",
    "klaer.tab.orders": "Comenzi",
    "klaer.catalog": "Catalog",
    "klaer.catalog.admin_title": "Catalog",
    "klaer.catalog.new_item": "Articol nou",
    "klaer.catalog.edit_item": "Editează articol",
    "klaer.catalog.col_name": "Nume",
    "klaer.catalog.col_sizes": "Mărimi",
    "klaer.catalog.col_colors": "Culori",
    "klaer.catalog.col_status": "Status",
    "klaer.catalog.status_active": "Activ",
    "klaer.catalog.status_hidden": "Ascuns",
    "klaer.catalog.field_name": "Nume",
    "klaer.catalog.field_category": "Categorie",
    "klaer.catalog.category_placeholder": "Ex. Îmbrăcăminte, Încălțăminte, Protecție",
    "klaer.catalog.order_categories": "Ordinea categoriilor",
    "klaer.catalog.upload_image": "Încarcă poză",
    "klaer.catalog.uploading": "Se încarcă…",
    "klaer.catalog.image_uploaded": "Poză încărcată",
    "klaer.catalog.remove_image": "Șterge poza",
    "klaer.catalog.primary": "Principală",
    "klaer.shop.all_categories": "Toate",
    "klaer.catalog.field_sizes": "Mărimi (separate prin virgulă)",
    "klaer.catalog.field_has_color": "Are opțiune de culoare",
    "klaer.catalog.field_colors": "Culori (separate prin virgulă)",
    "klaer.catalog.field_image": "URL imagine (opțional)",
    "klaer.catalog.field_note": "Notă (opțional)",
    "klaer.catalog.field_order": "Ordine (mai mic = primul)",
    "klaer.catalog.show_in_shop": "Afișează în magazin",
    "klaer.catalog.color_placeholder": "Negru, Albastru, Gri",
    "klaer.catalog.item_saved": "Articol actualizat",
    "klaer.catalog.item_created": "Articol creat",
    "klaer.catalog.item_deleted": "Șters",
    "klaer.catalog.save_failed": "Eroare",
    "klaer.catalog.confirm_delete": "Ștergi acest articol?",
    "klaer.catalog.confirm_delete_permanent": "Ștergi DEFINITIV? Nu se poate anula:",
    "klaer.profile.title_sizes": "Mărimile mele",
    "klaer.profile.hint_sizes": "Se completează automat în magazin când comanzi — dar poți suprascrie oricând în formular.",
    "klaer.profile.title_body": "Măsuri corp (opțional)",
    "klaer.profile.hint_body": "Utile dacă comandăm uniformă pe măsură.",
    "klaer.profile.height": "Înălțime (cm)",
    "klaer.profile.waist": "Talie (cm)",
    "klaer.profile.leg": "Interior picior (cm)",
    "klaer.profile.shoe_eu": "Bocanci (EU)",
    "klaer.profile.title_color": "Culoare preferată",
    "klaer.profile.color_placeholder": "ex. Negru",
    "klaer.profile.select": "— alege —",
    "klaer.profile.save_btn": "Salvează măsurile",
    "klaer.profile.saved": "Măsurile tale au fost salvate",
    "klaer.profile.save_failed": "Salvare eșuată",
    "klaer.shop.sizes_label": "Mărimi:",
    "klaer.shop.colors_label": "Culori:",
    "klaer.order.kicker": "Comandă",
    "klaer.order.size": "Mărime",
    "klaer.order.color": "Culoare",
    "klaer.order.quantity": "Cantitate",
    "klaer.order.note": "Comentariu (opțional)",
    "klaer.order.note_placeholder": "ex. Carâmb înalt, model de vară...",
    "klaer.order.submit": "Trimite comanda",
    "klaer.order.placed": "{name} comandat",
    "klaer.order.failed": "Comanda a eșuat",
    "klaer.orders.tab_incoming": "Comenzi primite",
    "klaer.orders.tab_mine": "Comenzile mele",
    "klaer.orders.filter_all": "Toate",
    "klaer.orders.filter_new": "În procesare",
    "klaer.orders.filter_delivered": "Livrate",
    "klaer.orders.empty_delivered": "Nicio comandă livrată încă.",
    "klaer.orders.empty_new": "Nicio comandă în așteptare.",
    "klaer.orders.empty_mine": "Nu ai comandat nimic încă.",
    "klaer.orders.empty_incoming": "Nicio comandă primită.",
    "klaer.orders.badge_new": "NOU",
    "klaer.orders.badge_delivered": "LIVRAT",
    "klaer.orders.pcs": "buc",
    "klaer.orders.deliver_confirm": "Marchezi ca livrat către {name}?",
    "klaer.orders.delivered_at": "Livrat {when}",
    "klaer.orders.delivered_by": "de {who}",
    "klaer.orders.deliver_btn": "Livrat",
    "klaer.orders.reopen_title": "Redeschide",
    "klaer.orders.delivered_toast": "Marcat ca livrat",
    "klaer.orders.reopen_toast": "Comandă redeschisă",
    "klaer.orders.delete_confirm": "Ștergi comanda?",
    "klaer.orders.deleted_toast": "Șters",
    "klaer.orders.op_failed": "Eroare",
    "klaer.admin_catalog_btn": "Administrează catalog",
    "klaer.cart.kicker": "Comandă",
    "klaer.cart.title": "Coș",
    "klaer.cart.empty": "Coșul este gol — adaugă articole din magazin.",
    "klaer.cart.add_btn": "Adaugă în coș",
    "klaer.cart.added_toast": "{name} adăugat în coș",
    "klaer.cart.submit_btn": "Trimite comanda",
    "klaer.cart.sent_toast": "Comandă trimisă ({count} buc)",
    "klaer.cart.clear": "Golește coșul",
    "klaer.cart.remove": "Șterge",
    "klaer.cart.order_note": "Comentariu pentru toată comanda (opțional)",
    "klaer.cart.order_note_placeholder": "ex. Am nevoie până lunea viitoare...",

    // Chat / Messenger (RO)
    "chat.action.reply": "Răspunde",
    "chat.action.mark_project": "Marchează la proiect",
    "chat.action.delete": "Șterge",
    "chat.composer.placeholder": "Scrie un mesaj…",
    "chat.composer.send": "Trimite",
    "chat.composer.voice_note": "Notă vocală",
    "chat.composer.photo": "Foto",
    "chat.composer.emoji": "Emoji",
    "chat.reply.replying_to": "Răspunzi",
    "chat.reply.cancel": "Anulează",
    "chat.header.project": "Proiect",
    "chat.header.group": "Grup",
    "chat.header.members": "membri",
    "chat.header.online": "Online",
    "chat.header.last_seen": "Ultima dată",
    "chat.toast.deleted": "Șters",
    "chat.toast.failed": "Eroare",
    "chat.toast.muted_on": "Conversație pusă pe silențios",
    "chat.toast.muted_off": "Sunet activat",
    "chat.toast.upload_failed": "Încărcare eșuată",
    "chat.toast.voice_failed": "Nu s-a putut trimite nota vocală",
    "chat.confirm.delete_message": "Ștergi mesajul?",
    "chat.marker.no_projects": "Niciun proiect",
    "chat.marker.title": "Marchează la proiect",
    "chat.marker.search": "Caută proiect…",
    "chat.reactions.count": "au reacționat",

    // Chat — extended (RO)
    "chat.header.dm": "Mesaj direct",
    "chat.header.active_now": "Activ acum",
    "chat.header.members_count": "{count} membri",
    "chat.header.mute": "Silențios",
    "chat.header.unmute": "Activează sunetul",
    "chat.composer.placeholder_short": "Scrie…",
    "chat.composer.send_photo": "Trimite foto",
    "chat.composer.hold_to_record": "Ține apăsat pentru a înregistra",
    "chat.composer.send_like": "Trimite 👍",
    "chat.audio.play": "Ascultă",
    "chat.audio.pause": "Pauză",
    "chat.message.deleted": "Mesaj șters",
    "chat.message.photo_alt": "foto",
    "chat.time.just_now": "acum",
    "chat.time.locale": "ro-RO",
    "chat.toast.send_failed": "Trimitere eșuată",
    "chat.newroom.tab_dm": "Mesaj direct",
    "chat.newroom.tab_group": "Grup",
    "chat.newroom.group_name_placeholder": "Nume grup (ex. Echipa A · Suhms Gate)",
    "chat.newroom.search_colleague": "Caută coleg…",
    "chat.newroom.select_colleague": "Alege un coleg",
    "chat.newroom.fill_group": "Completează numele și alege cel puțin un coleg",
    "chat.newroom.create_btn": "Creează",

    // Chat FAB / drawer (RO)
    "fab.open_full": "Deschide chat complet",
    "fab.minimize": "Minimizează",
    "fab.close": "Închide",
    "fab.open_msg": "Deschide mesaje",
    "fab.new_conversation": "Conversație nouă",
    "fab.mute_all": "Silențios pentru toți",
    "fab.unmute_all": "Activează sunetul",
    "fab.search_convos": "Caută în conversații",
    "fab.no_messages": "Niciun mesaj",
    "fab.enable_push": "Activează notificările",
    "fab.push_enabled": "Notificări activate — vei primi push la mesaje noi",
    "fab.push_failed": "Nu s-au putut activa",
    "fab.dm_open_failed": "Conversația nu a putut fi deschisă",
    "fab.project_chat_failed": "Chat-ul proiectului nu a putut fi deschis",
    "fab.chat_tab": "Chat",

    // Missing-phone banner (Home) (RO)
    "banner.phone.title": "Adaugă numărul tău de telefon",
    "banner.phone.subtitle": "Colegii au nevoie ca să te sune sau să-ți dea SMS. Îți ia 5 secunde.",
    "banner.phone.placeholder": "+40 712 345 678",
    "banner.phone.save": "Salvează",
    "banner.phone.saved": "Mulțumim! Colegii te pot suna acum la {phone}.",

    // PWA install (RO)
    "install.button": "Instalează aplicația Doserne",
    "install.ios_hint": "Apasă butonul de partajare (□↑) jos în Safari și alege „Adaugă la ecran principal”. Iconița „Doserne Academy” va apărea pe telefonul tău.",
    "install.generic_hint": "În browserul tău: deschide meniul (⋮) → „Instalează aplicația” sau „Adaugă la ecran principal”.",

    // ------- Week Calendar (RO) -------
    "cal.today": "Azi",
    "cal.week": "Săptămâna",
    "cal.empty_week": "Nicio notă săptămâna aceasta — click pe o zi pentru a adăuga.",
    "cal.add_event": "Adaugă eveniment",
    "cal.no_events": "Niciun eveniment pentru această zi.",
    "cal.title_ph": "Titlu",
    "cal.notes_ph": "Note / descriere…",
    "cal.date": "Dată",
    "cal.start_time": "Ora start",
    "cal.end_time": "Ora sfârșit",
    "cal.date_tt": "Ziua evenimentului",
    "cal.start_tt": "Când începe. Lăsat gol = toată ziua. Reminder-ul se calculează din această oră.",
    "cal.end_tt": "Opțional — doar afișat ca interval",
    "cal.allday_hint": "💡 Lăsate goale = eveniment „toată ziua”. Reminderele folosesc Ora start.",
    "cal.color": "Culoare",
    "cal.save": "Salvează",
    "cal.cancel": "Anulează",
    "cal.delete_q": "Ștergi acest eveniment?",
    "cal.deleted": "Șters",
    "cal.saved": "Salvat",
    "cal.title_required": "Titlul e obligatoriu",
    "cal.kind_note": "Notă",
    "cal.kind_meeting": "Întâlnire",
    "cal.kind_private": "Privat",
    "cal.kind_share": "Share",
    "cal.share_header": "Partajează cu admini",
    "cal.share_loading": "Încarc colegii…",
    "cal.share_empty": "Niciun admin disponibil în platformă.",
    "cal.share_summary_one": "Va primi notificare: 1 admin",
    "cal.share_summary_many": "Vor primi notificare: {{n}} admini",
    "cal.remind": "Reminder",
    "cal.remind_tt": "Cu cât timp înainte de start apare notificarea în clopoțel",
    "cal.remind_off": "Oprit",
    "cal.remind_at_start": "La start",
    "cal.remind_5m": "5 min înainte",
    "cal.remind_15m": "15 min înainte",
    "cal.remind_30m": "30 min înainte",
    "cal.remind_1h": "1 oră înainte",
    "cal.remind_1d": "1 zi înainte",
    "cal.pop_kicker": "Reminder",
    "cal.pop_dismiss": "Închide",
    "cal.pop_snooze_5": "Amână 5 min",
    "cal.pop_snooze_15": "Amână 15 min",
    "cal.pop_open": "Deschide evenimentul",
    "cal.pop_snoozed_5": "Amânat 5 min",
    "cal.pop_snoozed_15": "Amânat 15 min",
    "cal.pop_dismissed": "Închis",
    "cal.jump_title": "Sari la data",
    "cal.jump_today": "Azi",
    "cal.agenda_today": "Azi — ce urmează",
    "cal.agenda_next": "Următorul în calendar",
    "cal.allday_short": "Toată ziua",
  },

  en: {
    "app.brand": "Doserne Academy",
    "app.tagline": "Internal team tool",
    "app.copyright": "Doserne Stillas AS — Internal team platform",

    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.add": "Add",
    "common.back": "Back",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.confirm": "Confirm",
    "common.loading": "Loading…",
    "common.optional": "optional",
    "common.required": "Required",
    "common.search": "Search",
    "common.new": "New",
    "common.finish": "Finish",
    "common.done": "Done",
    "common.saved": "Saved",
    "common.error": "Error",
    "common.min": "min",

    "greet.hi": "Hi",
    "greet.what_now": "What would you like to do today?",
    "greet.today_focus": "Today's focus",

    "auth.welcome_back": "Welcome back",
    "auth.enter_email_hint": "Enter your email — we'll send you a one-time code.",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.remember_30": "Keep me signed in for 30 days",
    "auth.send_code": "Send code",
    "auth.enter_code": "Enter code",
    "auth.code_sent_to": "We sent a 6-digit code + sign-in link to",
    "auth.code_label": "Code (6 digits)",
    "auth.can_use_link": "You can also click the link in the email",
    "auth.log_in": "Log in",
    "auth.use_other_email": "Use a different email",
    "auth.resend_code": "Resend code",
    "auth.invalid_code": "Invalid code",
    "auth.expired_code": "Code has expired",
    "auth.only_for_employees": "Doserne employees only. Need access? Contact Bogdan.",
    "auth.log_out": "Log out",

    "nav.home": "Home",
    "nav.daily_plan": "Daily plan",
    "nav.projects": "Projects",
    "nav.customers": "Customers",
    "nav.progress": "Daily progress",
    "nav.colleagues": "Colleagues",
    "nav.underlag": "Underlag",
    "nav.kontroll": "Kontroll",
    "nav.sja": "SJA",
    "nav.admin": "Admin panel",
    "nav.lager": "Warehouse",
    "nav.kurs": "Training",
    "nav.klaer": "Clothing",
    "nav.chat": "Messages",
    "nav.reports": "Reports & suggestions",
    "nav.new_badge": "New",
    "nav.capacity": "Project Overview",
    "nav.po2": "Project Overview 2",
    "nav.profile": "My profile",
    "nav.contracts": "Contracts",
    "nav.email": "Email",
    "nav.timesheet": "Accounting control",
    "nav.timeliste": "Timesheet",
    "ts.months": "Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec",
    "ts.badge": "Accounting control",
    "ts.title": "Monthly timesheet",
    "ts.subtitle": "All employees' hours for the whole month. Click a day to view and correct the work interval.",
    "ts.tab_employees": "Employees",
    "ts.tab_projects": "Projects",
    "ts.tab_review": "Check hours",
    "ts.import_btn": "Import from Tripletex",
    "ts.import_running": "Importing...",
    "ts.import_confirm": "Overwrite this month's hours with Tripletex data?",
    "ts.import_result_title": "Tripletex import result",
    "ts.import_matched": "Employees matched",
    "ts.import_entries": "Hours imported",
    "ts.import_projects": "Projects linked",
    "ts.import_unmatched": "Not matched (not in /doserne with same name/email)",
    "ts.import_none_unmatched": "All employees were matched.",
    "ts.import_err": "Import failed.",
    "ts.sync_history": "Sync history",
    "ts.sync_never": "No sync yet",
    "ts.sync_last": "Last sync",
    "ts.sync_auto": "Automatic",
    "ts.sync_manual": "Manual",
    "ts.sync_hours": "hours",
    "ts.sync_vacation": "vacation",
    "ts.sync_sick": "sick",
    "ts.sync_auto_hint": "Runs automatically every day at 03:00 (Oslo)",
    "ts.link_btn": "Link employees",
    "ts.link_title": "Tripletex employees — manual linking",
    "ts.link_desc": "Link a Tripletex employee to the right /doserne user when the name is spelled differently. The link is remembered on every future sync.",
    "ts.link_unmatched": "Not linked",
    "ts.link_matched": "Linked",
    "ts.link_choose": "Choose colleague…",
    "ts.link_save": "Link",
    "ts.link_unlink": "Unlink",
    "ts.link_saved": "Linked! Run the import again to pull the hours.",
    "ts.link_none_unmatched": "All employees are linked 🎉",
    "ts.link_by_email": "email",
    "ts.link_by_name": "name",
    "ts.link_by_manual": "manual",
    "ts.link_err": "Linking failed.",
    "ts.sync_names_btn": "Sync names from Tripletex",
    "ts.sync_names_title": "Match names to Tripletex",
    "ts.sync_names_desc": "Rename /doserne members to match their Tripletex spelling exactly. Preview first, then apply.",
    "ts.sync_names_preview": "Preview changes",
    "ts.sync_names_none": "All names already match Tripletex 🎉",
    "ts.sync_names_apply": "Apply {n} rename(s)",
    "ts.sync_names_applied": "Renamed {n} member(s) to match Tripletex.",
    "ts.sync_names_err": "Name sync failed.",
    "ts.bclog_title": "Paid-break code history",
    "ts.bclog_none": "No codes used this month",
    "ts.bclog_who": "Employee",
    "ts.bclog_code": "Code",
    "ts.bclog_project": "Project",
    "ts.bclog_day": "Day",
    "ts.bclog_when": "Logged",
    "ts.bcsum_workers": "employees",
    "ts.bcsum_uses": "uses",
    "ts.bcsum_valid": "Valid",
    "ts.bcsum_export": "Export CSV",
    "ts.bcsum_nolabel": "(no label)",
    "ts.bcsum_all_projects": "All projects",
    "ts.col_missing": "Missing",
    "ts.col_missing_title": "Working days with no entry at all (month / year)",
    "ts.backfill_btn": "Import full year",
    "ts.backfill_confirm": "Import Tripletex hours from 1 Jan to today? Runs in the background.",
    "ts.backfill_running": "Importing…",
    "ts.backfill_started": "Import started! Watch progress in Sync History.",
    "ts.review_by_employee": "By employee",
    "ts.review_by_project": "By project",
    "ts.review_choose_employee": "— choose employee —",
    "ts.review_choose_project": "— choose project —",
    "ts.review_open": "Open the month's hours",
    "ts.review_empty": "No hours for this selection.",
    "ts.review_pick_first": "Choose an employee or a project, then open the month's hours.",
    "ts.review_break": "break",
    "ts.weekdays": "Mo,Tu,We,Th,Fr,Sa,Su",
    "ts.col_employee": "Employee",
    "ts.col_norm": "Norm",
    "ts.col_ot50": "OT 1.5×",
    "ts.col_ot100": "OT 2×",
    "ts.col_fer": "Vac",
    "ts.col_boa": "Sick",
    "ts.col_cost": "Cost",
    "ts.empty_members": "No employees / hours for this month.",
    "ts.payslip_btn_title": "Generate payslip",
    "ts.legend_normal": "Normal hours",
    "ts.legend_ot50": "OT 1.5× (50%)",
    "ts.legend_ot100": "OT 2× (100%)",
    "ts.legend_ferie": "Vacation (F)",
    "ts.legend_sick": "Sick (S)",
    "ts.legend_red": "Sunday / red day (100%)",
    "ts.legend_sat": "Saturday (normal day)",
    "ts.legend_depot": "Depot (no OT)",
    "ts.depot_tooltip": "Depot — hours always paid as normal (no 50% OT)",
    "ts.legend_compare": "Tick 2–3 employees to compare",
    "ts.ot_rule_note": "Overtime shows on the day the hours are worked (hours over 7.5h/day). Real (approved) overtime = everything over 37.5h worked in the week; if the week doesn't reach 37.5h (a missing day) the hours stay normal. Depot counts toward the 37.5h gate (up to 7.5h/day) but never becomes overtime.",
    "ts.abbr_vacation": "V",
    "ts.abbr_sick": "S",
    "ts.lock_title": "Lock weeks (approval)",
    "ts.lock_hint": "— a locked week can no longer be edited by employees and is included on the payslip",
    "ts.lock_locked": "locked",
    "ts.lock_open": "open",
    "ts.lock_month_only": "month",
    "ts.lock_split_hint": "Split week — locking affects only this month's days; the rest stays open for workers. Overtime is still calculated across the full week.",
    "ts.lock_toast_locked": "Week {range} locked",
    "ts.lock_toast_unlocked": "Week {range} unlocked",
    "ts.lock_err": "Could not change the lock.",
    "ts.cmp_title": "Worked hours comparison",
    "ts.cmp_clear": "Clear",
    "ts.cmp_max": "You can compare at most 3 employees.",
    "ts.cmp_ot50_short": "OT1.5",
    "ts.cmp_ot100_short": "OT2",
    "ts.day_edit": "Edit day",
    "ts.day_total": "Total {h}h",
    "ts.day_none": "No hours on this day.",
    "ts.kind_work": "Work",
    "ts.kind_vacation": "Vacation",
    "ts.kind_sick": "Sick",
    "ts.no_project": "no project",
    "ts.add_segment": "Add interval",
    "ts.cancel": "Cancel",
    "ts.save": "Save",
    "ts.seg_deleted": "Interval deleted.",
    "ts.seg_del_fail": "Could not delete.",
    "ts.seg_del_confirm": "Delete this interval?",
    "ts.saved": "Changes saved.",
    "ts.save_fail": "Could not save the changes.",
    "ts.overlap": "Overlapping intervals: {a} and {b}. Correct or delete one.",
    "ts.proj_search": "Search project by name...",
    "ts.proj_none_found": "No projects found.",
    "ts.proj_none": "No project hours this month.",
    "ts.proj_employees": "{n} employees",
    "ts.day_one": "day",
    "ts.day_many": "days",
    "ts.proj_total": "Total labor cost month:",
    "ts.payslip_title": "Payslip",
    "ts.payslip_tax": "Tax kr",
    "ts.payslip_save": "Save",
    "ts.payslip_saved": "Payslip saved",
    "ts.payslip_company_bank": "Company acct.",
    "ts.payslip_utlegg_desc": "Description",
    "ts.payslip_utlegg_add": "Add expense",
    "ts.payslip_print": "Print / PDF",
    "ts.payslip_no_salary": "This employee has no gross salary/hour set.",
    "ts.payslip_no_salary_hint": "Set 'Gross salary / hour' in the employee profile (Colleagues page) and try again.",
    "ts.payslip_err": "Could not generate the payslip.",
    "ts.hours_export_title": "Export hours",
    "ts.hours_export_err": "Could not load hours.",
    "ts.hours_from": "From",
    "ts.hours_to": "To",
    "ts.hours_show": "Show",
    "ts.hours_total": "total",
    "ts.hours_days": "days",
    "ts.hours_empty": "No hours in this period.",
    "ts.totals_gross": "Total gross (month)",
    "ts.totals_company": "Total company cost",
    "ts.totals_emps": "employees with salary",
    "ts.totals_company_hint": "Gross + feriepenger 12% + pensjon + arbeidsgiveravgift 14.1%",
    "ts.payslip_note": "Payslip generated from {n} locked (approved) weeks. Piecework, holiday pay and expenses will be added later.",
    "ts.payslip_warn": "Note: {weeks} week(s) are not locked — {hours}h are NOT on the payslip. Lock them in the Employees tab to include them.",
    "ts.load_err": "Could not load the timesheet.",
    "nav.coming_soon": "Soon",

    "home.no_plan_yet": "No plan published yet",
    "home.see_all_plans": "All plans",
    "home.today": "Today",
    "home.latest_plan": "Latest plan",
    "home.whats_new": "What's new",
    "home.feed_title": "From the job — latest photos",
    "home.feed_see_all": "See all photos",
    "home.feed_empty": "No photos posted yet",
    "home.no_news": "No news yet",

    "plan.title": "Daily plan",
    "plan.subtitle_admin": "Publish and manage today's jobs",
    "plan.subtitle_member": "See what teams will do today",
    "plan.new": "New plan",
    "plan.priority.normal": "Normal",
    "plan.priority.important": "Important",
    "plan.priority.urgent": "Urgent",
    "plan.empty": "No plan published yet.",
    "plan.empty_admin_hint": 'Click "New plan" to publish the first one.',
    "plan.published_by": "Published by",

    "projects.title": "Projects",
    "projects.subtitle": "All jobs we work on — linked to Timesheet, Inspection, SJA and progress.",
    "projects.search_placeholder": "Search name, customer, address…",
    "projects.all_status": "All statuses",
    "projects.new": "New project",
    "projects.empty": "No projects found.",
    "projects.add_first": "Add first project",
    "projects.status.active": "Active",
    "projects.status.on_hold": "On hold",
    "projects.status.done": "Done",
    "projects.status.archived": "Archived",
    "projects.back_to_list": "Back to projects",
    "projects.details_tab": "Details",
    "projects.progress_tab": "Progress",

    "progress.title": "Daily progress",
    "progress.subtitle": "Add photos and notes from the job — automatically organized per day in the project.",
    "progress.project": "Project",
    "progress.date": "Date",
    "progress.today": "Today",
    "progress.yesterday": "Yesterday",
    "progress.n_days_ago": "{n} days ago",
    "progress.note_optional": "Note (optional)",
    "progress.note_placeholder": "Brief description of what was done…",
    "progress.photos": "Photos",
    "progress.add_photo": "Add",
    "progress.publish": "Publish",
    "progress.select_project_first": "Select a project above to see earlier posts.",
    "progress.empty_project": "No progress posted yet.",
    "progress.earlier_posts": "Earlier posts — this project",
    "progress.published_success": "Progress published! 🎉",
    "progress.select_project_error": "Choose a project",
    "progress.add_content_error": "Add at least a photo or a note",

    "kurs.title": "Training",
    "kurs.subtitle": "Internal courses for Doserne employees. Pass to unlock the next course.",
    "kurs.status_line": "{done} of {total} courses passed",
    "kurs.your_training": "Your training status",
    "kurs.n_certificates": "{n} certificate",
    "kurs.n_certificates_plural": "{n} certificates",
    "kurs.start_here": "Start here",
    "kurs.status.not_started": "Not started",
    "kurs.status.in_progress": "In progress",
    "kurs.status.passed": "Passed",
    "kurs.badge.mandatory": "Mandatory",
    "kurs.badge.kurs_n": "Course {n}",
    "kurs.lesson_count": "{n} lessons",
    "kurs.quiz_count": "{n} questions",
    "kurs.start_course": "Start course",
    "kurs.continue": "Continue",
    "kurs.locked": "Requires previous course passed",
    "kurs.coming_soon": "Coming soon",
    "kurs.passed_with": "Passed — {pct}%",
    "kurs.step_n_of": "Step {current} / {total}",
    "kurs.lesson_n_of": "Lesson {n} · {course}",
    "kurs.lesson_x_of_y": "Lesson {current} of {total}",
    "kurs.intro_ready": "Introduction ready to complete",
    "kurs.done_bang": "Done!",
    "kurs.you_finished_prefix": "You completed",
    "kurs.back_to_catalog": "Back to courses",
    "kurs.lessons_in_this_course": "Lessons in this course",
    "kurs.lesson_shortcut": "Lesson {n}",
    "kurs.n_questions": "{n} questions at the end",
    "kurs.no_quiz": "No test",
    "kurs.passed_badge": "Passed",
    "kurs.mini_quiz_title": "End-of-lesson questions",
    "kurs.mini_quiz_intro": "Answer {n} short questions — score at least {pct}% to move on.",
    "kurs.mini_quiz_pass": "Passed!",
    "kurs.mini_quiz_retry_needed": "Not passed yet",
    "kurs.mini_quiz_score": "Your score: {score}% (minimum {threshold}%)",
    "kurs.continue_next": "Continue to next lesson",
    "kurs.continue_to_quiz": "Continue to the test",
    "kurs.back_to_index": "Back to lessons",
    "kurs.back_to_lesson": "Back to the lesson",
    "kurs.next_question": "Next question",
    "kurs.result_score": "Your score",
    "kurs.result_score_detail": "{correct} of {total} correct",
    "kurs.result_completed_on": "Completed on",
    "kurs.result_min_pass": "Minimum {pct}% to pass",
    "kurs.mini_quiz_reread_hint": "Read the lesson again before retrying. Take your time with each section.",
    "kurs.mini_quiz_retry_lesson": "Retry the lesson and try again",
    "push.on": "Notifications on",
    "push.enable": "Enable notifications",
    "push.blocked": "Notifications blocked",
    "push.blocked_help": "Permission was denied. Change it in your browser settings.",
    "push.toast.enabled": "Notifications enabled — you'll be alerted when a new plan is published",
    "push.toast.disabled": "Notifications disabled",
    "push.toast.failed": "Could not enable",
    "kurs.next_lesson": "Next lesson",
    "kurs.start_quiz": "Start quiz",
    "kurs.quiz_title": "Final quiz",
    "kurs.quiz_headline": "Test your knowledge",
    "kurs.quiz_intro": "{n} questions · Pass with at least {pct}%",
    "kurs.quiz_question_n": "Question {n} of {total}",
    "kurs.quiz_submit": "Submit",
    "kurs.quiz_answer_all": "Answer all questions before submitting.",
    "kurs.quiz_passed": "Passed! 🎉",
    "kurs.quiz_failed": "Not passed",
    "kurs.quiz_score_line": "You got {correct} of {total} correct ({pct}%). Threshold: {threshold}%.",
    "kurs.quiz_correct_answer": "Correct answer",
    "kurs.quiz_retry": "Try again",
    "kurs.cert.title": "Certificate",
    "kurs.cert.open_pdf": "Open certificate (PDF)",
    "kurs.cert.my": "My certificates",
    "kurs.cert.my_subtitle": "Proof of courses you've passed",
    "kurs.cert.empty": "No certificates yet.",
    "kurs.cert.see_courses": "See available courses",
    "kurs.locked_page.hint": "This course unlocks once you've passed: {prereqs}",
    "kurs.note": "Note",
    "kurs.warning": "Warning",
    "kurs.you_are_here": "You are here",

    "profile.title": "My profile",
    "profile.subtitle": "Settings, menu and courses",
    "profile.tab.overview": "Overview",
    "profile.tab.menu": "Menu",
    "profile.tab.kurs": "Courses",
    "profile.tab.devices": "Devices",
    "profile.tab.language": "Language",
    "profile.menu.hint": "Reorganize or hide menu entries. Changes are saved for your user.",
    "profile.menu.save": "Save menu",
    "profile.menu.saved": "Menu saved",
    "profile.menu_side": "Menu position",
    "profile.menu_side.left": "Left",
    "profile.menu_side.right": "Right",
    "profile.language.title": "Choose language",
    "profile.language.hint": "The language is used everywhere in the app and saved to your account.",
    "profile.devices.empty": "No devices registered yet.",
    "profile.devices.last_seen": "Last seen",
    "profile.role.admin": "Administrator",
    "profile.role.member": "Doserne Academy member",
    "profile.kurs.coming_soon_title": "Courses coming soon",
    "profile.kurs.coming_soon_body": "Internal courses, videos and certificates will appear here.",

    "onboard.welcome": "Welcome to Doserne Academy",
    "onboard.step_intro": "Let's set 2 quick preferences — then you're ready to go.",
    "onboard.step1_title": "Choose your language",
    "onboard.step1_hint": "You can change this any time from your profile.",
    "onboard.step2_title": "Menu position",
    "onboard.step2_hint": "Which side would you like the menu button?",
    "onboard.step2_left": "Left side",
    "onboard.step2_right": "Right side",
    "onboard.step3_title": "Your profile",
    "onboard.step3_hint": "A quick intro so your teammates know who you are.",
    "onboard.step3_name": "Full name",
    "onboard.step3_phone": "Phone number",
    "onboard.step3_note": "Both fields are required. You can update them later from your profile.",
    "onboard.finish": "Get started",
    "onboard.skip": "Skip",

    // Klær page (EN)
    "klaer.title": "Clothing & gear",
    "klaer.kicker": "Gear",
    "klaer.tab.shop": "Order clothing",
    "klaer.tab.profile": "My sizes",
    "klaer.tab.orders": "Orders",
    "klaer.catalog": "Catalog",
    "klaer.catalog.admin_title": "Catalog",
    "klaer.catalog.new_item": "New item",
    "klaer.catalog.edit_item": "Edit item",
    "klaer.catalog.col_name": "Name",
    "klaer.catalog.col_sizes": "Sizes",
    "klaer.catalog.col_colors": "Colors",
    "klaer.catalog.col_status": "Status",
    "klaer.catalog.status_active": "Active",
    "klaer.catalog.status_hidden": "Hidden",
    "klaer.catalog.field_name": "Name",
    "klaer.catalog.field_category": "Category",
    "klaer.catalog.category_placeholder": "E.g. Clothing, Shoes, Safety gear",
    "klaer.catalog.order_categories": "Category order",
    "klaer.catalog.upload_image": "Upload photo",
    "klaer.catalog.uploading": "Uploading…",
    "klaer.catalog.image_uploaded": "Image uploaded",
    "klaer.catalog.remove_image": "Remove image",
    "klaer.catalog.primary": "Main",
    "klaer.shop.all_categories": "All",
    "klaer.catalog.field_sizes": "Sizes (comma-separated)",
    "klaer.catalog.field_has_color": "Has color option",
    "klaer.catalog.field_colors": "Colors (comma-separated)",
    "klaer.catalog.field_image": "Image URL (optional)",
    "klaer.catalog.field_note": "Note (optional)",
    "klaer.catalog.field_order": "Sort order (lower = first)",
    "klaer.catalog.show_in_shop": "Show in shop",
    "klaer.catalog.color_placeholder": "Black, Blue, Grey",
    "klaer.catalog.item_saved": "Item updated",
    "klaer.catalog.item_created": "Item created",
    "klaer.catalog.item_deleted": "Deleted",
    "klaer.catalog.save_failed": "Failed",
    "klaer.catalog.confirm_delete": "Delete this item?",
    "klaer.catalog.confirm_delete_permanent": "Delete PERMANENTLY? This cannot be undone:",
    "klaer.profile.title_sizes": "My sizes",
    "klaer.profile.hint_sizes": "Auto-filled in the shop when you order — you can always override on the form.",
    "klaer.profile.title_body": "Body measurements (optional)",
    "klaer.profile.hint_body": "Useful when we order made-to-measure uniforms.",
    "klaer.profile.height": "Height (cm)",
    "klaer.profile.waist": "Waist (cm)",
    "klaer.profile.leg": "Inseam (cm)",
    "klaer.profile.shoe_eu": "Shoe (EU)",
    "klaer.profile.title_color": "Preferred color",
    "klaer.profile.color_placeholder": "e.g. Black",
    "klaer.profile.select": "— select —",
    "klaer.profile.save_btn": "Save my sizes",
    "klaer.profile.saved": "Your sizes have been saved",
    "klaer.profile.save_failed": "Save failed",
    "klaer.shop.sizes_label": "Sizes:",
    "klaer.shop.colors_label": "Colors:",
    "klaer.order.kicker": "Order",
    "klaer.order.size": "Size",
    "klaer.order.color": "Color",
    "klaer.order.quantity": "Quantity",
    "klaer.order.note": "Comment (optional)",
    "klaer.order.note_placeholder": "e.g. High shaft, summer model...",
    "klaer.order.submit": "Send order",
    "klaer.order.placed": "{name} ordered",
    "klaer.order.failed": "Order failed",
    "klaer.orders.tab_incoming": "Incoming orders",
    "klaer.orders.tab_mine": "My orders",
    "klaer.orders.filter_all": "All",
    "klaer.orders.filter_new": "Pending",
    "klaer.orders.filter_delivered": "Delivered",
    "klaer.orders.empty_delivered": "No delivered orders yet.",
    "klaer.orders.empty_new": "No pending orders.",
    "klaer.orders.empty_mine": "You haven't ordered anything yet.",
    "klaer.orders.empty_incoming": "No incoming orders.",
    "klaer.orders.badge_new": "NEW",
    "klaer.orders.badge_delivered": "DELIVERED",
    "klaer.orders.pcs": "pcs",
    "klaer.orders.deliver_confirm": "Mark as delivered to {name}?",
    "klaer.orders.delivered_at": "Received {when}",
    "klaer.orders.delivered_by": "by {who}",
    "klaer.orders.deliver_btn": "Delivered",
    "klaer.orders.reopen_title": "Reopen",
    "klaer.orders.delivered_toast": "Marked as delivered",
    "klaer.orders.reopen_toast": "Order reopened",
    "klaer.orders.delete_confirm": "Delete this order?",
    "klaer.orders.deleted_toast": "Deleted",
    "klaer.orders.op_failed": "Failed",
    "klaer.admin_catalog_btn": "Manage catalog",
    "klaer.cart.kicker": "Order",
    "klaer.cart.title": "Cart",
    "klaer.cart.empty": "Your cart is empty — add items from the shop.",
    "klaer.cart.add_btn": "Add to cart",
    "klaer.cart.added_toast": "{name} added to cart",
    "klaer.cart.submit_btn": "Send order",
    "klaer.cart.sent_toast": "Order sent ({count} pcs)",
    "klaer.cart.clear": "Empty cart",
    "klaer.cart.remove": "Remove",
    "klaer.cart.order_note": "Comment for the whole order (optional)",
    "klaer.cart.order_note_placeholder": "e.g. Needed before next Monday...",

    // Chat / Messenger (EN)
    "chat.action.reply": "Reply",
    "chat.action.mark_project": "Mark to project",
    "chat.action.delete": "Delete",
    "chat.composer.placeholder": "Write a message…",
    "chat.composer.send": "Send",
    "chat.composer.voice_note": "Voice note",
    "chat.composer.photo": "Photo",
    "chat.composer.emoji": "Emoji",
    "chat.reply.replying_to": "Replying to",
    "chat.reply.cancel": "Cancel",
    "chat.header.project": "Project",
    "chat.header.group": "Group",
    "chat.header.members": "members",
    "chat.header.online": "Online",
    "chat.header.last_seen": "Last seen",
    "chat.toast.deleted": "Deleted",
    "chat.toast.failed": "Failed",
    "chat.toast.muted_on": "Conversation muted",
    "chat.toast.muted_off": "Sound on again",
    "chat.toast.upload_failed": "Upload failed",
    "chat.toast.voice_failed": "Could not send voice note",
    "chat.confirm.delete_message": "Delete the message?",
    "chat.marker.no_projects": "No projects",
    "chat.marker.title": "Mark to project",
    "chat.marker.search": "Search project…",
    "chat.reactions.count": "reacted",

    // Chat — extended (EN)
    "chat.header.dm": "Direct message",
    "chat.header.active_now": "Active now",
    "chat.header.members_count": "{count} members",
    "chat.header.mute": "Mute conversation",
    "chat.header.unmute": "Unmute",
    "chat.composer.placeholder_short": "Message…",
    "chat.composer.send_photo": "Send photo",
    "chat.composer.hold_to_record": "Hold to record a voice note",
    "chat.composer.send_like": "Send 👍",
    "chat.audio.play": "Play",
    "chat.audio.pause": "Pause",
    "chat.message.deleted": "Message deleted",
    "chat.message.photo_alt": "photo",
    "chat.time.just_now": "now",
    "chat.time.locale": "en-GB",
    "chat.toast.send_failed": "Send failed",
    "chat.newroom.tab_dm": "Direct message",
    "chat.newroom.tab_group": "Group",
    "chat.newroom.group_name_placeholder": "Group name (e.g. Team A · Suhms Gate)",
    "chat.newroom.search_colleague": "Search colleague…",
    "chat.newroom.select_colleague": "Pick a colleague",
    "chat.newroom.fill_group": "Fill in the name and pick at least one colleague",
    "chat.newroom.create_btn": "Create",

    // Chat FAB / drawer (EN)
    "fab.open_full": "Open full chat",
    "fab.minimize": "Minimize",
    "fab.close": "Close",
    "fab.open_msg": "Open messages",
    "fab.new_conversation": "New conversation",
    "fab.mute_all": "Mute everyone",
    "fab.unmute_all": "Unmute",
    "fab.search_convos": "Search conversations",
    "fab.no_messages": "No messages",
    "fab.enable_push": "Enable notifications",
    "fab.push_enabled": "Push enabled — you'll get chat notifications",
    "fab.push_failed": "Could not enable",
    "fab.dm_open_failed": "Could not open the conversation",
    "fab.project_chat_failed": "Could not open project chat",
    "fab.chat_tab": "Chat",

    // Missing-phone banner (Home) (EN)
    "banner.phone.title": "Add your phone number",
    "banner.phone.subtitle": "Your colleagues need it to call or text you. Takes 5 seconds.",
    "banner.phone.placeholder": "+47 123 45 678",
    "banner.phone.save": "Save",
    "banner.phone.saved": "Thank you! Your colleagues can now reach you at {phone}.",

    // PWA install (EN)
    "install.button": "Install the Doserne app",
    "install.ios_hint": "Tap the share button (□↑) at the bottom of Safari and pick \"Add to Home Screen\". The Doserne Academy icon will appear on your home screen.",
    "install.generic_hint": "In your browser: open menu (⋮) → \"Install app\" or \"Add to Home Screen\".",

    // ------- Week Calendar (EN) -------
    "cal.today": "Today",
    "cal.week": "Week",
    "cal.empty_week": "No notes this week — click a day to add one.",
    "cal.add_event": "Add event",
    "cal.no_events": "No events for this day.",
    "cal.title_ph": "Title",
    "cal.notes_ph": "Notes / description…",
    "cal.date": "Date",
    "cal.start_time": "Start time",
    "cal.end_time": "End time",
    "cal.date_tt": "The day the event belongs to",
    "cal.start_tt": "When it starts. Blank = all-day. Reminder is counted from this time.",
    "cal.end_tt": "Optional — only shown as an interval",
    "cal.allday_hint": "💡 Leave blank = \"all-day\" event. Reminders use Start time.",
    "cal.color": "Color",
    "cal.save": "Save",
    "cal.cancel": "Cancel",
    "cal.delete_q": "Delete this event?",
    "cal.deleted": "Deleted",
    "cal.saved": "Saved",
    "cal.title_required": "Title is required",
    "cal.kind_note": "Note",
    "cal.kind_meeting": "Meeting",
    "cal.kind_private": "Private",
    "cal.kind_share": "Share",
    "cal.share_header": "Share with admins",
    "cal.share_loading": "Loading colleagues…",
    "cal.share_empty": "No admin available on the platform.",
    "cal.share_summary_one": "Will get a reminder: 1 admin",
    "cal.share_summary_many": "Will get a reminder: {{n}} admins",
    "cal.remind": "Reminder",
    "cal.remind_tt": "How long before the start a notification pops up in the bell",
    "cal.remind_off": "Off",
    "cal.remind_at_start": "At start",
    "cal.remind_5m": "5 min before",
    "cal.remind_15m": "15 min before",
    "cal.remind_30m": "30 min before",
    "cal.remind_1h": "1 hour before",
    "cal.remind_1d": "1 day before",
    "cal.pop_kicker": "Reminder",
    "cal.pop_dismiss": "Dismiss",
    "cal.pop_snooze_5": "Snooze 5 min",
    "cal.pop_snooze_15": "Snooze 15 min",
    "cal.pop_open": "Open event",
    "cal.pop_snoozed_5": "Snoozed 5 min",
    "cal.pop_snoozed_15": "Snoozed 15 min",
    "cal.pop_dismissed": "Dismissed",
    "cal.jump_title": "Jump to date",
    "cal.jump_today": "Today",
    "cal.agenda_today": "Today — what's next",
    "cal.agenda_next": "Next on the calendar",
    "cal.allday_short": "All day",
  },
};

const LangCtx = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANG_KEY = "DOSERNE_LANG";
const MENU_SIDE_KEY = "DOSERNE_MENU_SIDE";

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(LANG_KEY) || "no");
  const [menuSide, setMenuSideState] = useState(() => localStorage.getItem(MENU_SIDE_KEY) || "left");
  const [ready, setReady] = useState(false);
  // Guard against a mount-time /prefs GET clobbering a user click that
  // happened while the request was still in flight (race condition).
  const userChangedLangRef = useRef(false);
  const userChangedMenuSideRef = useRef(false);

  // On boot, if there is a Doserne token, sync from server profile
  // — but only for values the user hasn't already changed locally.
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("DOSERNE_TOKEN");
      if (token) {
        try {
          const { data } = await axios.get(`${API}/doserne/prefs`, { headers: { Authorization: `Bearer ${token}` } });
          if (data.lang && !userChangedLangRef.current) {
            setLangState(data.lang);
            localStorage.setItem(LANG_KEY, data.lang);
          }
          if (data.menu_side && !userChangedMenuSideRef.current) {
            setMenuSideState(data.menu_side);
            localStorage.setItem(MENU_SIDE_KEY, data.menu_side);
          }
        } catch { /* silent */ }
      }
      setReady(true);
    })();
  }, []);

  const setLang = useCallback(async (next) => {
    userChangedLangRef.current = true;
    setLangState(next);
    localStorage.setItem(LANG_KEY, next);
    const token = localStorage.getItem("DOSERNE_TOKEN");
    if (token) {
      try { await axios.put(`${API}/doserne/prefs`, { lang: next }, { headers: { Authorization: `Bearer ${token}` } }); } catch {}
    }
  }, []);

  const setMenuSide = useCallback(async (next) => {
    userChangedMenuSideRef.current = true;
    setMenuSideState(next);
    localStorage.setItem(MENU_SIDE_KEY, next);
    const token = localStorage.getItem("DOSERNE_TOKEN");
    if (token) {
      try { await axios.put(`${API}/doserne/prefs`, { menu_side: next }, { headers: { Authorization: `Bearer ${token}` } }); } catch {}
    }
  }, []);

  const t = useCallback((key, params = {}) => {
    const table = DICT[lang] || DICT.no;
    let str = table[key] ?? DICT.no[key] ?? key;
    for (const [k, v] of Object.entries(params)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
    return str;
  }, [lang]);

  return (
    <LangCtx.Provider value={{ lang, setLang, menuSide, setMenuSide, t, ready }}>
      {children}
    </LangCtx.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangCtx);
  if (!ctx) throw new Error("useLang outside provider");
  return ctx;
}

export const useT = () => useLang().t;

/** Extract the correct language from a multilingual field on backend content.
 * Value can be a plain string or an object like {no, ro, en}. */
export function pick(field, lang) {
  if (field == null) return "";
  if (typeof field === "string") return field;
  if (typeof field === "object") return field[lang] || field.no || field.en || field.ro || Object.values(field)[0] || "";
  return String(field);
}
